

import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import Select from "react-select";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
/* ══════════════════════════════════════════
   CONSTANTS
   ══════════════════════════════════════════ */
const HIGH_RISK_REASONS = [
  "AGE < 19 YRS","AGE > 35 YRS","BLOOD DISORDER","BOH","BRONCHIAL ASTHMA",
  "CARDIAC DISORDER","CERVICAL INCOMPETENCE","CHRONIC HYPERTENSION","PIH",
  "PRE ECLAMPSIA","IMMENENT ECLAMPSIA","ECLAMSIA","DM TYPE- I","DM TYPE- II",
  "EPILEPSY","GDM","GRAND MULTIPARA","HYPERTHYROIDISM","HYPOTHYROIDISM",
  "INFECTIOUS DISEASE POSITIVE","MULTIPLE PREGNANCIES","OBESITY",
  "PLACENTA ACRETA","PLACENTA PRAEVIA","PLACENTAL ABRUPTION",
  "PRECIOUS PREGNANCY","PREVIOUS LSCS","PREVIOUS PREGNANCY COMPLICATION",
  "RA","RENAL DISORDER","RH NEGATIVE","SHORT STATURE","SLE"
].map(r => ({ value: r, label: r }));

const CONSANGUINITY_OPTS = ["CM", "NCM"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const MARRIAGE_OPTS = ["First", "Second", "Third"];

// ─── INVESTIGATION CONFIG — REORDERED ───
// LEFT block (NO Given Date column, only Done Date + Result)
const FIXED_TESTS_LEFT = ["HIV", "HBSAg", "HCV", "VDRL", "ECG", "ECHO", "ACA IgG", "ACA IgM", "ANA"];
// RIGHT block (HAS Given Date + Due Date range + Done Date + Result)
const FIXED_TESTS_RIGHT = ["DUAL MARKER", "NT SCAN", "ANOMALY SCAN"];

const DATED_COLUMNS = ["HB", "RBS", "FBS", "PPBS", "HbA1c", "TSH", "CREATININE", "URINE", "TC", "PLT", "GCT", "LFT", "HB ELECTROPHORESIS"];

const getResultColor = (val) => {
  if (!val) return null;
  const v = String(val).trim().toLowerCase();
  if (v === 'positive') return { bg:'#d1fae5', color:'#065f46', border:'#10b981' };
  if (v === 'negative') return { bg:'#fee2e2', color:'#991b1b', border:'#ef4444' };
  return null;
};






const toSubscript = (n) => {
  if (n === '' || n === null || n === undefined) return '';
  const map = { '0':'₀','1':'₁','2':'₂','3':'₃','4':'₄','5':'₅','6':'₆','7':'₇','8':'₈','9':'₉' };
  return String(n).split('').map(c => map[c] || c).join('');
};

const formatGplaSubscript = (details) => {
  if (!details) return '';
  const { g='', p='', l='', a='', d='' } = details;
  const safe = v => v === '' || v === null || v === undefined ? '0' : String(v);
  return `G${toSubscript(safe(g))}P${toSubscript(safe(p))}L${toSubscript(safe(l))}A${toSubscript(safe(a))}D${toSubscript(safe(d))}`;
};

const addDays = (dateStr, days) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const calculateGA = (referenceDateStr) => {
  if (!referenceDateStr) return '';
  const parts = String(referenceDateStr).split('-');
  if (parts.length !== 3) return '';
  const ref = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  if (isNaN(ref.getTime())) return '';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = today - ref;
  if (diffMs < 0) return 'Not yet started';
  const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;
  return `${weeks} weeks ${days} days`;
};

// Calculate GA for ANY target date (used in General Exam row "DATE" → GA auto-calc)
const calculateGAForDate = (referenceDateStr, targetDateStr) => {
  if (!referenceDateStr || !targetDateStr) return '';
  const refParts = String(referenceDateStr).split('-');
  const targetParts = String(targetDateStr).split('-');
  if (refParts.length !== 3 || targetParts.length !== 3) return '';
  const ref = new Date(Number(refParts[0]), Number(refParts[1])-1, Number(refParts[2]));
  const target = new Date(Number(targetParts[0]), Number(targetParts[1])-1, Number(targetParts[2]));
  if (isNaN(ref.getTime()) || isNaN(target.getTime())) return '';
  const diffMs = target - ref;
  if (diffMs < 0) return 'Not yet started';
  const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;
  return `${weeks}w ${days}d`;
};

const PRESET_IMMUNIZATIONS = ["Td 1","Td 2","INFLUENZA","HEPATITIS B"];
const PAST_HISTORY_TYPES = ["MEDICAL","SURGICAL","DRUG ALLERGY","BLOOD TRANSFUSION"];
const FAMILY_HISTORY_TYPES = ["DIABETES","HYPERTENSION","TWINS","GENETIC DISORDER"];
const PERSONAL_HISTORY_TYPES = ["DIET","APPETITE","BOWEL & BLADDER","HABITS","SLEEP"];
const MENSTRUAL_CYCLE_OPTS = ["REGULAR","IRREGULAR"];
const MENSTRUAL_DURATION_OPTS = ["< 3 Days","3-4 Days","4-5 Days","5-6 Days","6-7 Days","7-8 Days","> 8 Days"];
const MENSTRUAL_FREQUENCY_OPTS = ["< 15 DAYS","28-30 DAYS","30-45 DAYS","45-60 DAYS","> 60 DAYS"];
const MENSTRUAL_FLOW_OPTS = ["MILD","MODERATE","SEVERE"];
const MENSTRUAL_PAIN_OPTS = ["PAINFULL","PAINLESS"];

const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const nowStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`; };

const SectionCard = ({ title, icon, children, color = '#405189', headerBg = '#e8eef6', borderColor = '#b8c9e0' }) => (
  <div className="card mb-3" style={{ border: `1px solid ${borderColor}` }}>
    <div className="card-header py-2" style={{ backgroundColor: headerBg }}>
      <h6 className="mb-0" style={{ color, fontWeight: 700 }}><i className={`${icon} me-2`}></i>{title}</h6>
    </div>
    <div className="card-body p-2">{children}</div>
  </div>
);

/* ══════════════════════════════════════════
   GPLA POPUP
   ══════════════════════════════════════════ */
function GplaScorePopup({ show, onClose, onDone, initialValues }) {
  const [vals, setVals] = useState({ g:'',p:'',l:'',a:'',d:'' });
  useEffect(() => { if (show) setVals({ g: initialValues?.g||'', p: initialValues?.p||'', l: initialValues?.l||'', a: initialValues?.a||'', d: initialValues?.d||'' }); }, [show, initialValues]);
  const opts = Array.from({length:10},(_,i)=>i+1);
  if (!show) return null;
  return (
    <div style={{ position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100001 }} onClick={onClose}>
      <div style={{ background:'#fff',borderRadius:12,width:'90%',maxWidth:600,boxShadow:'0 10px 40px rgba(0,0,0,0.25)',overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
        <div style={{ background:'linear-gradient(135deg,#405189,#6366f1)',color:'#fff',padding:'14px 20px',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
          <h5 style={{margin:0}}><i className="ri-calculator-line me-2"></i>GPLA Score</h5>
          <button onClick={onClose} style={{background:'none',border:'none',color:'#fff',fontSize:24,cursor:'pointer'}}>&times;</button>
        </div>
        <div style={{padding:20}}>
          <table className="table table-bordered text-center" style={{fontSize:14}}>
            <thead style={{backgroundColor:'#d6e4f0'}}><tr>{['G (Gravida)','P (Para)','L (Living)','A (Abortion)','D (Death)'].map((h,i)=><th key={i} style={{width:'20%'}}>{h}</th>)}</tr></thead>
            <tbody><tr>{['g','p','l','a','d'].map((k,i)=>(
              <td key={i}><select className="form-select form-select-sm" value={vals[k]} onChange={e=>setVals(p=>({...p,[k]:e.target.value}))} style={{textAlign:'center',fontWeight:600}}><option value="">--</option>{opts.map(n=><option key={n} value={n}>{n}</option>)}</select></td>
            ))}</tr></tbody>
          </table>
          <div className="text-center my-3"><span style={{fontSize:18,fontWeight:700,color:'#405189'}}>Preview: G{vals.g||0} P{vals.p||0} L{vals.l||0} A{vals.a||0} D{vals.d||0}</span></div>
          <div className="d-flex justify-content-end gap-2">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={()=>{ onDone(`G${vals.g||0}P${vals.p||0}L${vals.l||0}A${vals.a||0}D${vals.d||0}`, vals); }}><i className="ri-check-line me-1"></i>Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   OBSTETRIC HISTORY
   ══════════════════════════════════════════ */
function ObstetricHistorySection({ rows, onChange, isEditing, onAddRow, onRemoveRow }) {
  const upd = (idx,f,v) => onChange(rows.map((r,i)=>i===idx?{...r,[f]:v}:r));
  const ss = {fontSize:12,padding:'2px 4px',minHeight:30};
  const is = {fontSize:12,padding:'2px 6px',minHeight:30};
  const th = {fontSize:11,padding:'4px 3px',textAlign:'center',backgroundColor:'#d6e4f0',color:'#405189',fontWeight:700,whiteSpace:'nowrap'};
  const td = {padding:'3px 2px',verticalAlign:'middle'};
  const DD = ({val,opts,onChange:oc}) => <select className="form-select form-select-sm" style={ss} value={val||''} onChange={e=>oc(e.target.value)}><option value="">--</option>{opts.map(o=><option key={o} value={o}>{o}</option>)}</select>;
  return (
    <SectionCard title="Obstetric History" icon="ri-stethoscope-line">
      {isEditing && <div className="text-end mb-1"><button className="btn btn-primary btn-sm" onClick={onAddRow}><i className="ri-add-line me-1"></i>Add Row</button></div>}
      <div style={{overflowX:'auto'}}>
        <table className="table table-bordered table-sm mb-0" style={{fontSize:12}}>
          <thead><tr>
            {['#','ABORTION','SEX','AGE YRS','FULL/PRE TERM','MODE','BABY WT','OUTCOME','COMPLICATION','REMARKS'].map((h,i)=><th key={i} style={th}>{h}</th>)}
            {isEditing && <th style={{...th,width:35}}></th>}
          </tr></thead>
          <tbody>{rows.map((row,idx)=>(
            <tr key={idx}>
              <td style={{...td,textAlign:'center',fontWeight:700,color:'#405189'}}>G{idx+1}</td>
              <td style={td}>{isEditing ? <DD val={row.abortion} opts={['SPONTANEOUS','VOLUNTARY','D & E','NIL']} onChange={v=>upd(idx,'abortion',v)}/> : <span>{row.abortion||'-'}</span>}</td>
              <td style={td}>{isEditing ? <DD val={row.sex} opts={['MALE','FEMALE']} onChange={v=>upd(idx,'sex',v)}/> : <span>{row.sex||'-'}</span>}</td>
              <td style={td}>{isEditing ? <input className="form-control form-control-sm" style={is} value={row.age_years||''} onChange={e=>upd(idx,'age_years',e.target.value)} placeholder="Age"/> : <span>{row.age_years||'-'}</span>}</td>
              <td style={td}>{isEditing ? <DD val={row.full_pre_term} opts={['FULL TERM','PRE TERM']} onChange={v=>upd(idx,'full_pre_term',v)}/> : <span>{row.full_pre_term||'-'}</span>}</td>
              <td style={td}>{isEditing ? <DD val={row.mode} opts={['NVD','C-SECTION','INSTRUMENT']} onChange={v=>upd(idx,'mode',v)}/> : <span>{row.mode||'-'}</span>}</td>
              <td style={td}>{isEditing ? <input className="form-control form-control-sm" style={is} value={row.baby_weight||''} onChange={e=>upd(idx,'baby_weight',e.target.value)} placeholder="kg"/> : <span>{row.baby_weight||'-'}</span>}</td>
              <td style={td}>{isEditing ? <DD val={row.outcome} opts={['LIVE','DEAD','STILL BIRTH','IUD']} onChange={v=>upd(idx,'outcome',v)}/> : <span>{row.outcome||'-'}</span>}</td>
              <td style={td}>{isEditing ? <DD val={row.complication} opts={['HYPERTENSION','ECLAMPSIA','GDM','ANEMIA','PPH','PLACENTA PRAEVIA','ABRUPTION','NONE']} onChange={v=>upd(idx,'complication',v)}/> : <span>{row.complication||'-'}</span>}</td>
              <td style={td}>{isEditing ? <input className="form-control form-control-sm" style={is} value={row.remarks||''} onChange={e=>upd(idx,'remarks',e.target.value)} placeholder="Remarks"/> : <span>{row.remarks||'-'}</span>}</td>
              {isEditing && <td style={{...td,textAlign:'center'}}>{rows.length>1 && <button className="btn btn-sm btn-outline-danger p-0" style={{width:22,height:22}} onClick={()=>onRemoveRow(idx)}><i className="ri-close-line" style={{fontSize:13}}></i></button>}</td>}
            </tr>
          ))}</tbody>
        </table>
      </div>
    </SectionCard>
  );
}

/* ══════════════════════════════════════════
   INVESTIGATION — UPDATED
   - Left block: no Given Date column
   - Right block: NEW Due Date range (from-to)
   - Order: LEFT = HIV, HBSAg, HCV, VDRL, ECG, ECHO, ACA IgG, ACA IgM, ANA
            RIGHT = DUAL MARKER, NT SCAN, ANOMALY SCAN
   ══════════════════════════════════════════ */
   
   function ResultDropdownCell({ value, onChange, isEditing, inputStyle }) {
    const [customMode, setCustomMode] = useState(false);
    useEffect(() => {
      if (!value) { setCustomMode(false); return; }
      const v = String(value).trim().toLowerCase();
      setCustomMode(v !== 'positive' && v !== 'negative');
    }, [value]);
  
    const color = getResultColor(value);
    if (!isEditing) {
      if (!value) return <span>-</span>;
      if (color) return <span style={{display:'inline-block',padding:'2px 8px',background:color.bg,color:color.color,border:`1px solid ${color.border}`,borderRadius:4,fontWeight:700,fontSize:11}}>{value}</span>;
      return <span>{value}</span>;
    }
    const ddStyle = {...inputStyle, backgroundColor: color?color.bg:'#fff', color: color?color.color:'inherit', fontWeight: color?700:'normal', border: color?`1px solid ${color.border}`:'1px solid #ced4da'};
    if (customMode) {
      return (
        <div className="d-flex gap-1">
          <input className="form-control form-control-sm" style={{...inputStyle,flex:1}} value={value||''} onChange={e=>onChange(e.target.value)} placeholder="Custom" autoFocus/>
          <button type="button" className="btn btn-sm btn-outline-secondary p-0 px-1" style={{fontSize:10}} onClick={()=>{setCustomMode(false);onChange('');}} title="Back">↺</button>
        </div>
      );
    }
    return (
      <select className="form-select form-select-sm" style={ddStyle} value={value||''} onChange={e=>{const v=e.target.value;if(v==='__custom'){setCustomMode(true);onChange('');}else onChange(v);}}>
        <option value="">--</option>
        <option value="Positive">Positive</option>
        <option value="Negative">Negative</option>
        <option value="__custom">✏️ Custom...</option>
      </select>
    );
  }
   
   function InvestigationSection({ rows, onChange, isEditing, immunizations, onImmunizationChange }) {
  const fixedRows = rows.filter(r => r.type === 'fixed');
  const datedRows = rows.filter(r => r.type === 'dated');

  const customColumns = (() => {
    const set = new Set();
    rows.filter(r => r.type === 'dated').forEach(r => {
      Object.keys(r).forEach(k => {
        if (k !== 'type' && k !== 'date' && !DATED_COLUMNS.includes(k)) set.add(k);
      });
    });
    return Array.from(set);
  })();
  
  const allDatedColumns = [...DATED_COLUMNS, ...customColumns];
  
  const addCustomColumn = () => {
    const colName = window.prompt("Enter new column name (e.g. 'VITAMIN D'):");
    if (!colName || !colName.trim()) return;
    const clean = colName.trim().toUpperCase();
    if (allDatedColumns.includes(clean)) { 
      toast.warning("Column already exists"); 
      return; 
    }
    // Add this column to all existing dated rows
    const updated = rows.map(r => r.type === 'dated' ? { ...r, [clean]: '' } : r);
    // If no dated rows exist yet, add a blank one so column persists
    if (!updated.some(r => r.type === 'dated')) {
      const blank = { type:'dated', date: todayStr() };
      [...DATED_COLUMNS, clean].forEach(c => { blank[c] = ''; });
      updated.push(blank);
    }
    onChange(updated);
  };
  
  const removeCustomColumn = (colName) => {
    if (!window.confirm(`Remove column "${colName}"? Data in this column will be lost.`)) return;
    const updated = rows.map(r => {
      if (r.type !== 'dated') return r;
      const copy = { ...r };
      delete copy[colName];
      return copy;
    });
    onChange(updated);
  };

  // Ensure all fixed tests have a row (auto-seed)
  useEffect(() => {
    if (!isEditing) return;
    const existing = new Set(fixedRows.map(r => r.test_name));
    const missing = [...FIXED_TESTS_LEFT, ...FIXED_TESTS_RIGHT].filter(t => !existing.has(t));
    if (missing.length > 0) {
      const newFixed = missing.map(t => ({ type:'fixed', test_name:t, given_date:'', due_date_from:'', due_date_to:'', done_date:'', result:'' }));
      onChange([...rows, ...newFixed]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const updFixed = (testName, field, value) => {
    onChange(rows.map(r => (r.type === 'fixed' && r.test_name === testName) ? { ...r, [field]: value } : r));
  };

  let datedSeq = 0;
  const updDated = (idx, field, value) => {
    let i = 0;
    onChange(rows.map(r => {
      if (r.type !== 'dated') return r;
      if (i === idx) { i++; return { ...r, [field]: value }; }
      i++;
      return r;
    }));
  };

  const addDatedRow = () => {
    const blank = { type:'dated', date: todayStr() };
    DATED_COLUMNS.forEach(c => { blank[c] = ''; });
    onChange([...rows, blank]);
  };

  const removeDatedRow = (idx) => {
    let i = 0;
    onChange(rows.filter(r => {
      if (r.type !== 'dated') return true;
      if (i === idx) { i++; return false; }
      i++;
      return true;
    }));
  };

  // Render LEFT block — NO Given Date column
  const renderLeftBlock = () => {
    const th = { fontSize:11, padding:'4px 6px', textAlign:'center', backgroundColor:'#d6e4f0', color:'#405189', fontWeight:700 };
    const td = { padding:'3px 4px', fontSize:12, verticalAlign:'middle' };
    const is = { fontSize:11, padding:'1px 4px', minHeight:28 };
    return (
      <table className="table table-bordered table-sm mb-0">
        <thead>
          <tr>
            <th style={{...th, width:100}}></th>
            <th style={th}>Done Date</th>
            <th style={th}>Result</th>
          </tr>
        </thead>
        <tbody>
          {FIXED_TESTS_LEFT.map(t => {
            const row = fixedRows.find(r => r.test_name === t) || { test_name:t, done_date:'', result:'' };
            return (
              <tr key={t}>
                <td style={{...td, fontWeight:700, background:'#f8f9fa'}}>{t}</td>
                <td style={td}>{isEditing ? <input type="date" className="form-control form-control-sm" style={is} value={row.done_date||''} onChange={e=>updFixed(t,'done_date',e.target.value)}/> : <span>{row.done_date||'-'}</span>}</td>
                <td style={td}>
  <ResultDropdownCell value={row.result} onChange={v=>updFixed(t,'result',v)} isEditing={isEditing} inputStyle={is}/>
</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // Render RIGHT block — with Given Date + Due Date Range + Done Date + Result
  const renderRightBlock = () => {
    const th = { fontSize:11, padding:'4px 6px', textAlign:'center', backgroundColor:'#d6e4f0', color:'#405189', fontWeight:700 };
    const td = { padding:'3px 4px', fontSize:12, verticalAlign:'middle' };
    const is = { fontSize:11, padding:'1px 4px', minHeight:28 };
    return (
      <table className="table table-bordered table-sm mb-0">
        <thead>
          <tr>
            <th style={{...th, width:115}}></th>
            <th style={th}>Given Date</th>
            <th style={th} colSpan={isEditing ? 2 : 1}>Due Date <small style={{fontSize:9}}>(From – To)</small></th>
            <th style={th}>Done Date</th>
            <th style={th}>Result</th>
          </tr>
        </thead>
        <tbody>
          {FIXED_TESTS_RIGHT.map(t => {
            const row = fixedRows.find(r => r.test_name === t) || { test_name:t, given_date:'', due_date_from:'', due_date_to:'', done_date:'', result:'' };
            return (
              <tr key={t}>
                <td style={{...td, fontWeight:700, background:'#f8f9fa'}}>{t}</td>
                <td style={td}>{isEditing ? <input type="date" className="form-control form-control-sm" style={is} value={row.given_date||''} onChange={e=>updFixed(t,'given_date',e.target.value)}/> : <span>{row.given_date||'-'}</span>}</td>
                {isEditing ? (
                  <>
                    <td style={td}><input type="date" className="form-control form-control-sm" style={is} value={row.due_date_from||''} onChange={e=>updFixed(t,'due_date_from',e.target.value)} title="Due From"/></td>
                    <td style={td}><input type="date" className="form-control form-control-sm" style={is} value={row.due_date_to||''} onChange={e=>updFixed(t,'due_date_to',e.target.value)} title="Due To"/></td>
                  </>
                ) : (
                  <td style={td}><span style={{fontSize:11}}>{row.due_date_from||'-'} to {row.due_date_to||'-'}</span></td>
                )}
                <td style={td}>{isEditing ? <input type="date" className="form-control form-control-sm" style={is} value={row.done_date||''} onChange={e=>updFixed(t,'done_date',e.target.value)}/> : <span>{row.done_date||'-'}</span>}</td>
                <td style={td}>{isEditing ? <input className="form-control form-control-sm" style={is} value={row.result||''} onChange={e=>updFixed(t,'result',e.target.value)} placeholder="Result"/> : <span>{row.result||'-'}</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const th = { fontSize:10, padding:'4px 4px', textAlign:'center', backgroundColor:'#b8d4f0', color:'#405189', fontWeight:700, whiteSpace:'nowrap' };
  const td = { padding:'2px 2px', fontSize:11, verticalAlign:'middle' };
  const is = { fontSize:10, padding:'1px 3px', minHeight:26 };

  return (
    <SectionCard title="Investigation" icon="ri-test-tube-line">
      <div className="row g-2 mb-3">
        <div className="col-md-6">
          <div className="mb-1"><small className="text-muted fw-bold">📋 Done-only Tests</small></div>
          <div style={{overflowX:'auto'}}>{renderLeftBlock()}</div>
        </div>
        <div className="col-md-6">
          <div className="mb-1"><small className="text-muted fw-bold">📅 Scheduled Tests (with Due Date range)</small></div>
          <div style={{overflowX:'auto'}}>{renderRightBlock()}</div>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-1">
        <h6 className="mb-0" style={{color:'#405189', fontSize:13}}>
          <i className="ri-calendar-line me-1"></i>Dated Lab Tests (compare over time)
        </h6>
        {isEditing }
      </div>



    {/* ───── Dated Lab Tests ───── */}
    <div className="d-flex justify-content-between align-items-center mb-1">
        <h6 className="mb-0" style={{color:'#405189', fontSize:13}}>
          <i className="ri-calendar-line me-1"></i>Dated Lab Tests
        </h6>
        {isEditing && (
          <div className="d-flex gap-1">
            <button className="btn btn-outline-primary btn-sm" onClick={addCustomColumn}>
              <i className="ri-add-circle-line me-1"></i>Add Column
            </button>
            <button className="btn btn-primary btn-sm" onClick={addDatedRow}>
              <i className="ri-add-line me-1"></i>Add Date Row
            </button>
          </div>
        )}
      </div>
      <div style={{overflowX:'auto'}}>
        <table className="table table-bordered table-sm mb-0">
          <thead>
            <tr>
              <th style={{...th, width:110}}>DATE</th>
              {allDatedColumns.map(c => (
                <th key={c} style={th}>
                  {c}
                  {isEditing && customColumns.includes(c) && (
                    <button
                      className="btn btn-sm p-0 ms-1"
                      style={{color:'#dc3545', fontSize:11, background:'none', border:'none'}}
                      onClick={() => removeCustomColumn(c)}
                      title="Remove column"
                    >×</button>
                  )}
                </th>
              ))}
              {isEditing && <th style={{...th, width:30}}></th>}
            </tr>
          </thead>
          <tbody>
            {datedRows.length === 0 && (
              <tr><td colSpan={isEditing ? allDatedColumns.length+2 : allDatedColumns.length+1} className="text-center text-muted py-2" style={{fontSize:12}}>No dated entries. Click "Add Date Row" to start.</td></tr>
            )}
            {datedRows.map((row, idx) => (
              <tr key={idx}>
                <td style={td}>{isEditing ? <input type="date" className="form-control form-control-sm" style={is} value={row.date||''} onChange={e=>updDated(idx,'date',e.target.value)}/> : <span>{row.date||'-'}</span>}</td>
                {allDatedColumns.map(c => (
                  <td key={c} style={td}>
                    {isEditing
                      ? <input className="form-control form-control-sm" style={is} value={row[c]||''} onChange={e=>updDated(idx,c,e.target.value)}/>
                      : <span>{row[c]||'-'}</span>}
                  </td>
                ))}
                {isEditing && <td style={{...td, textAlign:'center'}}><button className="btn btn-sm btn-outline-danger p-0" style={{width:20,height:20}} onClick={()=>removeDatedRow(idx)}><i className="ri-close-line" style={{fontSize:12}}></i></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ───── IMMUNIZATION (inline, inside Investigation tab) ───── */}
      <div className="mt-4 pt-3" style={{borderTop:'2px dashed #ffc107'}}>
        <ImmunizationSection 
          rows={immunizations || []} 
          onChange={onImmunizationChange} 
          isEditing={isEditing}
        />
      </div>
    </SectionCard>
  );
}





      
/* ══════════════════════════════════════════
   IMMUNIZATION — UPDATED with Due Date range
   ══════════════════════════════════════════ */
function ImmunizationSection({ rows, onChange, isEditing }) {
  const upd = (idx,f,v) => onChange(rows.map((r,i)=>i===idx?{...r,[f]:v}:r));
  const add = () => onChange([...rows, { 
    name: '',  given_date: '', due_date_from:'', due_date_to:'', done_date: '', remarks: '' 
  }]);
  const rem = idx => onChange(rows.filter((_,i)=>i!==idx));
  const th = {fontSize:11,padding:'4px 6px',textAlign:'center',backgroundColor:'#fef3cd',color:'#856404',fontWeight:700,whiteSpace:'nowrap'};
  const td = {padding:'3px 4px',verticalAlign:'middle',fontSize:12};
  const is = {fontSize:12,padding:'2px 6px'};
  return (
    <SectionCard title="Immunization" icon="ri-syringe-line" headerBg="#fef3cd" color="#856404" borderColor="#ffc107">
      {isEditing && <div className="text-end mb-1"><button className="btn btn-warning btn-sm" onClick={add}><i className="ri-add-line me-1"></i>Add</button></div>}
      <div style={{overflowX:'auto'}}>
        <table className="table table-bordered table-sm mb-0">
          <thead><tr>
            <th style={{...th,width:40}}>#</th>
            <th style={{...th,width:180}}>IMMUNIZATION</th>
            <th style={{...th,width:120}}>GIVEN DATE</th>
            <th style={{...th,width:200}} colSpan={isEditing ? 2 : 1}>DUE DATE <small style={{fontSize:9}}>(From – To)</small></th>
            <th style={{...th,width:120}}>DONE DATE</th>
            {/* <th style={{...th,width:140}}>RESULT</th> */}
            <th style={{...th,width:160}}>REMARKS</th>
            {isEditing && <th style={{...th,width:35}}></th>}
          </tr></thead>
          <tbody>
            {rows.map((row,idx)=>(
              <tr key={idx}>
                <td style={{...td,textAlign:'center',fontWeight:600}}>{idx+1}</td>
                <td style={td}>{isEditing ? (
                  <div>
                    <select className="form-select form-select-sm mb-1" style={is} value={PRESET_IMMUNIZATIONS.includes(row.name)?row.name:'__custom'} onChange={e=>{if(e.target.value==='__custom')upd(idx,'name','');else upd(idx,'name',e.target.value)}}>
                      <option value="">-- Select --</option>
                      {PRESET_IMMUNIZATIONS.map(t=><option key={t} value={t}>{t}</option>)}
                      <option value="__custom">✏️ Custom...</option>
                    </select>
                    {!PRESET_IMMUNIZATIONS.includes(row.name) && <input className="form-control form-control-sm" style={is} value={row.name||''} onChange={e=>upd(idx,'name',e.target.value)} placeholder="Enter name"/>}
                  </div>
                ) : <span style={{fontWeight:600}}>{row.name||'-'}</span>}</td>
                <td style={td}>{isEditing ? <input type="date" className="form-control form-control-sm" style={is} value={row.given_date||''} onChange={e=>upd(idx,'given_date',e.target.value)}/> : <span>{row.given_date||'-'}</span>}</td>
                {isEditing ? (
                  <>
                    <td style={td}><input type="date" className="form-control form-control-sm" style={is} value={row.due_date_from||''} onChange={e=>upd(idx,'due_date_from',e.target.value)} title="Due From"/></td>
                    <td style={td}><input type="date" className="form-control form-control-sm" style={is} value={row.due_date_to||''} onChange={e=>upd(idx,'due_date_to',e.target.value)} title="Due To"/></td>
                  </>
                ) : (
                  <td style={td}><span style={{fontSize:11}}>{row.due_date_from||'-'} to {row.due_date_to||'-'}</span></td>
                )}
                <td style={td}>{isEditing ? <input type="date" className="form-control form-control-sm" style={is} value={row.done_date||''} onChange={e=>upd(idx,'done_date',e.target.value)}/> : <span>{row.done_date||'-'}</span>}</td>
                {/* <td style={td}>{isEditing ? <input className="form-control form-control-sm" style={is} value={row.result||''} onChange={e=>upd(idx,'result',e.target.value)} placeholder="Result"/> : <span>{row.result||'-'}</span>}</td> */}
                <td style={td}>{isEditing ? <input className="form-control form-control-sm" style={is} value={row.remarks||''} onChange={e=>upd(idx,'remarks',e.target.value)} placeholder="Remarks"/> : <span>{row.remarks||'-'}</span>}</td>
                {isEditing && <td style={{...td,textAlign:'center'}}><button className="btn btn-sm btn-outline-danger p-0" style={{width:22,height:22}} onClick={()=>rem(idx)}><i className="ri-close-line" style={{fontSize:13}}></i></button></td>}
              </tr>
            ))}
            {rows.length===0 && <tr><td colSpan={isEditing?9:8} className="text-center text-muted py-2">No immunizations added</td></tr>}
          </tbody>
        </table>
      </div>


   
      </SectionCard>
  );
}

/* ══════════════════════════════════════════
   GENERAL EXAMINATION — UPDATED
   - Auto GA from current date when adding row
   - ADVICE = textarea (wider)
   - NEW INVESTIGATION column = textarea
   - F/U column = date picker
   ══════════════════════════════════════════ */
function GeneralExamSection({ rows, onChange, isEditing, onViewPrescription,onPrintMedicine, onUploadPrescription, onNewMedicine, onOldMedicine, lmpRef }) {
  const upd = (idx,f,v) => {
    onChange(rows.map((r,i) => {
      if (i !== idx) return r;
      const u = { ...r, [f]: v };
      // Auto-recalc GA when date changes (if LMP available)
      if (f === 'date' && lmpRef) {
        u.ga = calculateGAForDate(lmpRef, v);
      }
      return u;
    }));
  };

  const add = () => {
    const today = todayStr();
    const autoGA = lmpRef ? calculateGAForDate(lmpRef, today) : '';
    onChange([...rows, { 
      ge_id: `ge_${Date.now()}_${Math.floor(Math.random()*1000)}`,
      date: today, ga: autoGA, weight:'', pallor:'', oedema:'', bp:'', pulse:'', spo2:'', temp:'', 
      fhr:'', usg:'', pa:'', pv:'', chief_complain:'', advice:'', investigation:'', remarks:'', follow_up:'', 
      prescription_id:'', prescription_file:'' 
    }]);
  };
  const rem = idx => onChange(rows.filter((_,i)=>i!==idx));

  const th = {fontSize:10,padding:'3px 2px',textAlign:'center',backgroundColor:'#d1ecf1',color:'#0c5460',fontWeight:700,whiteSpace:'nowrap'};
  const td = {padding:'2px 2px',verticalAlign:'middle',fontSize:11};
  const is = {fontSize:11,padding:'1px 4px',minHeight:26};
  const wideIs = {fontSize:11,padding:'2px 4px',minHeight:50,resize:'vertical'};

  return (
    <SectionCard title="General Examination" icon="ri-heart-pulse-line" headerBg="#d1ecf1" color="#0c5460" borderColor="#bee5eb">
      {isEditing && (
        <div className="text-end mb-1">
          <button className="btn btn-info btn-sm" onClick={add}><i className="ri-add-line me-1"></i>Add Row</button>
          {lmpRef && <small className="text-muted ms-2">GA auto-calculated from LMP: {lmpRef}</small>}
        </div>
      )}
      <div style={{overflowX:'auto'}}>
        <table className="table table-bordered table-sm mb-0" style={{fontSize:11}}>
          <thead><tr>
            <th style={{...th,width:30}}>#</th>
            <th style={{...th,width:105}}>DATE</th>
            <th style={{...th,width:80}}>GA</th>
            <th style={{...th,width:55}}>WT</th>
            <th style={{...th,width:60}}>PALLOR</th>
            <th style={{...th,width:60}}>OEDEMA</th>
            <th style={{...th,width:60}}>BP</th>
            <th style={{...th,width:55}}>PULSE</th>
            <th style={{...th,width:55}}>SPO2</th>
            <th style={{...th,width:55}}>TEMP</th>
            <th style={{...th,width:55}}>FHR</th>
            <th style={{...th,width:60}}>USG</th>
            <th style={{...th,width:55}}>PA</th>
            <th style={{...th,width:55}}>PV</th>
            <th style={{...th,width:140}}>CHIEF COMPLAIN</th>
            <th style={{...th,width:160}}>ADVICE</th>
            <th style={{...th,width:160}}>INVESTIGATION</th>
            <th style={{...th,width:140}}>REMARKS</th>
            <th style={{...th,width:105}}>F/U</th>
            <th style={{...th,width:130}}>PRESCRIPTION</th>
            {isEditing && <th style={{...th,width:30}}></th>}
          </tr></thead>
          <tbody>
            {rows.map((row,idx)=>(
              <tr key={idx}>
                <td style={{...td,textAlign:'center',fontWeight:600}}>{idx+1}</td>
                {/* DATE — auto-calcs GA when changed */}
                <td style={td}>
                  {isEditing ? <input type="date" className="form-control form-control-sm" style={is} value={row.date||''} onChange={e=>upd(idx,'date',e.target.value)}/> : <span>{row.date||'-'}</span>}
                </td>
                {/* GA — auto-filled, but editable */}
                <td style={td}>
                  {isEditing ? <input className="form-control form-control-sm" style={{...is, backgroundColor:'#f0fdf4', fontWeight:600, color:'#059669'}} value={row.ga||''} onChange={e=>upd(idx,'ga',e.target.value)} placeholder="auto"/> : <span style={{fontWeight:600,color:'#059669'}}>{row.ga||'-'}</span>}
                </td>
                {/* Regular small fields */}
                {['weight','pallor','oedema','bp','pulse','spo2','temp','fhr'].map(f => (
  <td key={f} style={td}>
    {isEditing ? <input className="form-control form-control-sm" style={is} value={row[f]||''} onChange={e=>upd(idx,f,e.target.value)} placeholder={f}/> : <span>{row[f]||'-'}</span>}
  </td>
))}
{/* USG with hover tooltip */}
<td style={{...td, maxWidth:80, overflow:'hidden'}}>
  {isEditing
    ? <input className="form-control form-control-sm" style={is} value={row.usg||''} onChange={e=>upd(idx,'usg',e.target.value)} placeholder="USG" title={row.usg||''}/>
    : <span title={row.usg||''} style={{display:'inline-block',maxWidth:'100%',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',cursor:row.usg?'help':'default'}}>{row.usg||'-'}</span>}
</td>
{['pa','pv'].map(f => (
  <td key={f} style={td}>
    {isEditing ? <input className="form-control form-control-sm" style={is} value={row[f]||''} onChange={e=>upd(idx,f,e.target.value)} placeholder={f}/> : <span>{row[f]||'-'}</span>}
  </td>
))}
                {/* Chief Complain */}
                <td style={td}>
                  {isEditing
                    ? <textarea className="form-control form-control-sm" style={wideIs} rows="2" value={row.chief_complain||''} onChange={e=>upd(idx,'chief_complain',e.target.value)} placeholder="Chief complain"/>
                    : <span>{row.chief_complain||'-'}</span>}
                </td>
                {/* ADVICE — textarea, wider */}
                <td style={td}>
                  {isEditing
                    ? <textarea className="form-control form-control-sm" style={wideIs} rows="2" value={row.advice||''} onChange={e=>upd(idx,'advice',e.target.value)} placeholder="Advice"/>
                    : <span>{row.advice||'-'}</span>}
                </td>
                {/* INVESTIGATION — NEW textarea */}
                <td style={td}>
                  {isEditing
                    ? <textarea className="form-control form-control-sm" style={wideIs} rows="2" value={row.investigation||''} onChange={e=>upd(idx,'investigation',e.target.value)} placeholder="Investigation"/>
                    : <span>{row.investigation||'-'}</span>}
                </td>
                {/* REMARKS */}
                <td style={td}>
                  {isEditing
                    ? <textarea className="form-control form-control-sm" style={wideIs} rows="2" value={row.remarks||''} onChange={e=>upd(idx,'remarks',e.target.value)} placeholder="Remarks"/>
                    : <span>{row.remarks||'-'}</span>}
                </td>
                {/* F/U — date picker */}
                <td style={td}>
                  {isEditing ? <input type="date" className="form-control form-control-sm" style={is} value={row.follow_up||''} onChange={e=>upd(idx,'follow_up',e.target.value)}/> : <span>{row.follow_up||'-'}</span>}
                </td>
                {/* PRESCRIPTION */}
                <td style={td}>
  <div className="d-flex gap-1 align-items-center flex-wrap">
    {isEditing && (
      <>
        <button className="btn btn-success btn-sm p-0 px-2" style={{fontSize:10,whiteSpace:'nowrap'}} onClick={()=>onNewMedicine&&onNewMedicine(idx)} title="New"><i className="ri-add-circle-line me-1"></i>New</button>
        <button className="btn btn-info btn-sm p-0 px-2" style={{fontSize:10,whiteSpace:'nowrap',color:'#fff'}} onClick={()=>onOldMedicine&&onOldMedicine(idx)} title="Old"><i className="ri-history-line me-1"></i>Old</button>
      </>
    )}
    {/* NEW: Print button — available even when not editing */}
    <button className="btn btn-warning btn-sm p-0 px-2" style={{fontSize:10,whiteSpace:'nowrap'}} onClick={()=>onPrintMedicine&&onPrintMedicine(idx)} title="Print Prescription"><i className="ri-printer-line me-1"></i>Print</button>
    {row.prescription_file && <a href={row.prescription_file} target="_blank" rel="noreferrer" className="btn btn-outline-success btn-sm p-0 px-1" style={{fontSize:10}} title="View Upload"><i className="ri-file-line"></i></a>}
  </div>
</td>
                {isEditing && <td style={{...td,textAlign:'center'}}><button className="btn btn-sm btn-outline-danger p-0" style={{width:20,height:20}} onClick={()=>rem(idx)}><i className="ri-close-line" style={{fontSize:12}}></i></button></td>}
              </tr>
            ))}
            {rows.length===0 && <tr><td colSpan={isEditing?21:20} className="text-center text-muted py-2">No examinations added</td></tr>}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

/* ══════════════════════════════════════════
   KEY-VALUE HISTORY
   ══════════════════════════════════════════ */
function KeyValueHistorySection({ title, icon, types, data, onChange, isEditing, headerBg, color, borderColor }) {
  const upd = (key,val) => onChange({...data, [key]: val});
  return (
    <SectionCard title={title} icon={icon} headerBg={headerBg||'#e8eef6'} color={color||'#405189'} borderColor={borderColor||'#b8c9e0'}>
      {types.map(type => (
        <div key={type} className="mb-2">
          <label className="form-label fw-bold mb-1" style={{fontSize:12,color:color||'#405189'}}>{type}</label>
          {isEditing ? (
            <textarea className="form-control form-control-sm" rows="2" placeholder={`Enter ${type.toLowerCase()} details...`} value={data[type]||''} onChange={e=>upd(type, e.target.value)} style={{fontSize:12}}/>
          ) : (
            <div style={{fontSize:13,padding:'4px 8px',background:'#f8f9fa',borderRadius:4,minHeight:28}}>{data[type]||<span className="text-muted">N/A</span>}</div>
          )}
        </div>
      ))}
    </SectionCard>
  );
}

/* ══════════════════════════════════════════
   MENSTRUAL HISTORY
   ══════════════════════════════════════════ */
function MenstrualHistorySection({ data, onChange, isEditing }) {
  const upd = (f,v) => onChange({...data, [f]:v});
  const DD = ({label,field,opts}) => (
    <div className="col-md-2 mb-2">
      <label className="form-label fw-bold mb-1" style={{fontSize:12}}>{label}</label>
      {isEditing ? (
        <select className="form-select form-select-sm" style={{fontSize:12}} value={data[field]||''} onChange={e=>upd(field,e.target.value)}>
          <option value="">-- Select --</option>
          {opts.map(o=><option key={o} value={o}>{o}</option>)}
        </select>
      ) : <div style={{fontSize:13,padding:'4px 8px',background:'#f8f9fa',borderRadius:4}}>{data[field]||'N/A'}</div>}
    </div>
  );
  return (
    <SectionCard title="Menstrual History" icon="ri-calendar-2-line" headerBg="#f8d7da" color="#721c24" borderColor="#f5c6cb">
      <div className="row">
        <div className="col-md-2 mb-2">
          <label className="form-label fw-bold mb-1" style={{fontSize:12}}>MENARCHE AGE</label>
          {isEditing ? <input className="form-control form-control-sm" style={{fontSize:12}} value={data.menarche_age||''} onChange={e=>upd('menarche_age',e.target.value)} placeholder="Age"/>
            : <div style={{fontSize:13,padding:'4px 8px',background:'#f8f9fa',borderRadius:4}}>{data.menarche_age||'N/A'}</div>}
        </div>
        <DD label="CYCLE" field="cycle" opts={MENSTRUAL_CYCLE_OPTS}/>
        <DD label="DURATION" field="duration" opts={MENSTRUAL_DURATION_OPTS}/>
        <DD label="FREQUENCY" field="frequency" opts={MENSTRUAL_FREQUENCY_OPTS}/>
        <DD label="FLOW" field="flow" opts={MENSTRUAL_FLOW_OPTS}/>
        <DD label="PAIN" field="pain" opts={MENSTRUAL_PAIN_OPTS}/>
      </div>
      <div className="row mt-1">
        <div className="col-md-3 mb-2">
          <label className="form-label fw-bold mb-1" style={{fontSize:12}}>LMP</label>
          {isEditing ? <input type="date" className="form-control form-control-sm" style={{fontSize:12}} value={data.lmp||''} onChange={e=>upd('lmp',e.target.value)}/>
            : <div style={{fontSize:13,padding:'4px 8px',background:'#f8f9fa',borderRadius:4}}>{data.lmp||'N/A'}</div>}
        </div>
        <div className="col-md-3 mb-2">
          <label className="form-label fw-bold mb-1" style={{fontSize:12}}>EDD</label>
          {isEditing ? <input type="date" className="form-control form-control-sm" style={{fontSize:12}} value={data.edd||''} onChange={e=>upd('edd',e.target.value)}/>
            : <div style={{fontSize:13,padding:'4px 8px',background:'#f8f9fa',borderRadius:4}}>{data.edd||'N/A'}</div>}
        </div>
      </div>
    </SectionCard>
  );
}

/* ══════════════════════════════════════════
   MAIN ANC FILE MODAL
   ══════════════════════════════════════════ */
function AncFileModal({ show, onHide, patient, formData }) {
  const defaultObRow = {abortion:'',sex:'',age_years:'',full_pre_term:'',mode:'',baby_weight:'',outcome:'',complication:'',remarks:''};
  const emptyRecord = {
    anc_id: null,
    guardian_title: patient?.opd_guardien_title || '',
    occupation: '', marriage: 'First', blood_group: '',
    address: patient?.opd_address || '',
    husband_name: '', husband_age: '', husband_occupation: '',
    husband_marriage: 'First', husband_blood_group: '', husband_address: '',
    consanguinity: '', cm_ncm: '', gpla: '',
    gpla_details: { g:'', p:'', l:'', a:'', d:'' },
    ga: '', mode: 'NATURAL', chief_complain: '',
    risk_status: 'Normal', high_risk_reasons: [], high_risk_remarks: '',
    remarks: '',
    delivery_note: { date:'', time:'', sex:'', baby_wt:'', hospital_name:'', full_pre_term:'', mode:'', outcome:'', complication:'', remarks:'' },
    lmp: '', edd: '', c_lmp: '', c_edd: '',
    obstetric_history: [{ ...defaultObRow }],
    investigations: [], immunizations: [], general_exams: [],
    past_history: {}, family_history: {}, personal_history: {},
    menstrual_history: { menarche_age:'', cycle:'', duration:'', frequency:'', flow:'', pain:'', lmp:'', edd:'' },
    created_at: '', isEditing: true, isNew: true,
    is_closed: false, close_reason: '', closed_at: '', closed_by: ''
  };

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showGplaPopup, setShowGplaPopup] = useState(false);
  const [gplaTargetIdx, setGplaTargetIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');

  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeTargetIdx, setCloseTargetIdx] = useState(null);
  const [closeReason, setCloseReason] = useState('');

  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printTargetIdx, setPrintTargetIdx] = useState(null);
  const [printSections, setPrintSections] = useState({});

  const [ldata, setLdata] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const [printHeaderLogo, setPrintHeaderLogo] = useState('');
  const [printFooterLogo, setPrintFooterLogo] = useState('');

  const [showMedPopup, setShowMedPopup] = useState(false);
  const [medPopupMode, setMedPopupMode] = useState('new');
  const [medPopupGeIdx, setMedPopupGeIdx] = useState(null);
  const [medPopupAncIdx, setMedPopupAncIdx] = useState(null);
  const [medPopupRows, setMedPopupRows] = useState([]);
  const [medPopupLoading, setMedPopupLoading] = useState(false);
  const [medPopupTemplate, setMedPopupTemplate] = useState(null);
  const [medicineOptions, setMedicineOptions] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);

  useEffect(() => {
    const sd = sessionStorage.getItem("ldata");
    if (sd) {
      const p = JSON.parse(sd);
      setLdata(p);
      if (String(p?.role_id) === '1' || p?.is_admin === 1) setIsAdmin(true);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.post(`${AppConst.BASE_URL + AppConst.getconfig}?config_type=lab`);
        if (res.data?.status) {
          setPrintHeaderLogo(res.data.header_logo || '');
          setPrintFooterLogo(res.data.footer_logo || '');
        }
      } catch(e) { /* ignore */ }
    })();
  }, []);

  const safeJson = (str, fallback) => { try { const p=JSON.parse(str||'null'); return p||fallback; } catch { return fallback; } };

  const fetchAncRecords = useCallback(async () => {
    if (!patient?.opd_uhid) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("action","get_anc_records");
      fd.append("uhid",patient.opd_uhid);
      const res = await axios.post(AppConst.BASE_URL+(AppConst.ancFileUrl||"Webapis/anc_file"),fd,{headers:{"Content-Type":"multipart/form-data"}});
      if (res.data?.status && Array.isArray(res.data.records)) {
        setRecords(res.data.records.map(r => ({
          anc_id:r.anc_id, guardian_title:r.anc_guardian_title||'', occupation:r.anc_occupation||'',
          marriage:r.anc_marriage||'First', address:r.anc_address||'', consanguinity:r.anc_consanguinity||'',
          cm_ncm:r.anc_cm_ncm||'', gpla:r.anc_gpla||'',
          gpla_details: safeJson(r.anc_gpla_details,{g:'',p:'',l:'',a:'',d:''}),
          ga:r.anc_ga||'', mode:r.anc_mode||'NATURAL', chief_complain:r.anc_chief_complain||'',
          risk_status:r.anc_risk_status||'Normal',
          high_risk_reasons: safeJson(r.anc_high_risk_reasons,[]),
          high_risk_remarks: r.anc_high_risk_remarks||'',
          remarks:r.anc_remarks||'',
          obstetric_history: safeJson(r.anc_obstetric_history,[{...defaultObRow}]),
          investigations: safeJson(r.anc_investigations,[]),
          immunizations: safeJson(r.anc_immunizations,[]),
          general_exams: (safeJson(r.anc_general_exams, []) || []).map(g => ({
            ...g,
            ge_id: g.ge_id || `ge_${Date.now()}_${Math.floor(Math.random()*1000)}_legacy`,
            advice: g.advice || '',
            investigation: g.investigation || '',
          })),
          past_history: safeJson(r.anc_past_history,{}),
          family_history: safeJson(r.anc_family_history,{}),
          personal_history: safeJson(r.anc_personal_history,{}),
          menstrual_history: safeJson(r.anc_menstrual_history,{menarche_age:'',cycle:'',duration:'',frequency:'',flow:'',pain:'',lmp:'',edd:''}),
          created_at:r.anc_created_at||'', isEditing:false, isNew:false,
          blood_group: r.anc_blood_group || '',
          husband_name: r.anc_husband_name || '',
          husband_age: r.anc_husband_age || '',
          husband_occupation: r.anc_husband_occupation || '',
          husband_marriage: r.anc_husband_marriage || 'First',
          husband_blood_group: r.anc_husband_blood_group || '',
          husband_address: r.anc_husband_address || '',
          lmp: r.anc_lmp || '', edd: r.anc_edd || '', c_lmp: r.anc_c_lmp || '', c_edd: r.anc_c_edd || '',
          delivery_note: safeJson(r.anc_delivery_note, {date:'',time:'',sex:'',baby_wt:'',hospital_name:'',full_pre_term:'',mode:'',outcome:'',complication:'',remarks:''}),
          is_closed: Number(r.anc_is_closed || 0) === 1,
          close_reason: r.anc_close_reason || '',
          closed_at: r.anc_closed_at || '',
          closed_by: r.anc_closed_by || '',
        })));
      } else setRecords([]);
    } catch(e) { setRecords([]); } finally { setLoading(false); }
  },[patient]);

  useEffect(()=>{ if(show&&patient) fetchAncRecords(); },[show,patient,fetchAncRecords]);

  const addNewRecord = () => {
    const newRec = {...emptyRecord, guardian_title:patient?.opd_guardien_title||'', address:patient?.opd_address||'', created_at:nowStr()};
    setRecords(prev=>[newRec,...prev]);
    setExpandedId("new_0");
    setActiveSection('basic');
  };
  const updateField = (idx,f,v) => setRecords(prev=>prev.map((r,i)=>i===idx?{...r,[f]:v}:r));

  const updateDateField = (idx, field, value) => {
    setRecords(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, [field]: value };
      if (field === 'lmp' && value) updated.edd = addDays(value, 280);
      if (field === 'c_edd' && value) updated.c_lmp = addDays(value, -280);
      if (field === 'c_lmp' && value) updated.c_edd = addDays(value, 280);
      const refDate = updated.c_lmp || updated.lmp;
      if (refDate) updated.ga = calculateGA(refDate);
      return updated;
    }));
  };

  const handleGplaDone = (combined, details) => {
    if (gplaTargetIdx !== null) {
      updateField(gplaTargetIdx, 'gpla_details', details);
      updateField(gplaTargetIdx, 'gpla', formatGplaSubscript(details));
    }
    setShowGplaPopup(false);
    setGplaTargetIdx(null);
  };

  const saveRecord = async (idx) => {
    const rec=records[idx]; if(!rec) return;
    setSaving(true);
    try {
      const fd=new FormData();
      fd.append("action",rec.isNew?"create_anc":"update_anc");
      if(rec.anc_id) fd.append("anc_id",String(rec.anc_id));
      fd.append("uhid",patient?.opd_uhid||'');
      fd.append("opd_id",patient?.opd_id||'');
      fd.append("patient_name",patient?.opd_patient_name||'');
      fd.append("patient_age",patient?.opd_guardien_age||'');
      ['guardian_title','occupation','marriage','address','consanguinity','cm_ncm','gpla','ga','mode','chief_complain','risk_status','high_risk_remarks','remarks'].forEach(k=>fd.append(k,rec[k]||''));
      fd.append("gpla_details",JSON.stringify(rec.gpla_details||{}));
      fd.append("high_risk_reasons",JSON.stringify(rec.high_risk_reasons||[]));
      fd.append("obstetric_history",JSON.stringify(rec.obstetric_history||[]));
      fd.append("investigations",JSON.stringify(rec.investigations||[]));
      fd.append("immunizations",JSON.stringify(rec.immunizations||[]));
      fd.append("general_exams",JSON.stringify(rec.general_exams||[]));
      fd.append("past_history",JSON.stringify(rec.past_history||{}));
      fd.append("family_history",JSON.stringify(rec.family_history||{}));
      fd.append("personal_history",JSON.stringify(rec.personal_history||{}));
      fd.append("menstrual_history",JSON.stringify(rec.menstrual_history||{}));
      fd.append("created_at",rec.created_at||'');
      ['blood_group','husband_name','husband_age','husband_occupation','husband_marriage','husband_blood_group','husband_address','lmp','edd','c_lmp','c_edd'].forEach(k=>fd.append(k,rec[k]||''));
      fd.append("delivery_note", JSON.stringify(rec.delivery_note || {}));
      const res=await axios.post(AppConst.BASE_URL+(AppConst.ancFileUrl||"Webapis/anc_file"),fd,{headers:{"Content-Type":"multipart/form-data"}});
      if(res.data?.status){toast.success(rec.isNew?"ANC Record Saved!":"ANC Record Updated!");await fetchAncRecords();}
      else toast.error(res.data?.message||"Failed");
    } catch(e) { toast.error("Error saving"); } finally { setSaving(false); }
  };

  const deleteRecord = async (idx) => {
    const rec=records[idx];
    if(rec.isNew){setRecords(p=>p.filter((_,i)=>i!==idx));return;}
    const r=await Swal.fire({title:"Delete this ANC record?",icon:"warning",showCancelButton:true,confirmButtonText:"Delete",confirmButtonColor:"#d33",customClass:{container:'swal-above-modal'}});
    if(!r.isConfirmed) return;
    try { const fd=new FormData(); fd.append("action","delete_anc"); fd.append("anc_id",String(rec.anc_id)); const res=await axios.post(AppConst.BASE_URL+(AppConst.ancFileUrl||"Webapis/anc_file"),fd); if(res.data?.status){toast.success("Deleted");await fetchAncRecords();} else toast.error("Failed"); } catch(e){toast.error("Error");}
  };

  const toggleEdit = (idx) => setRecords(p=>p.map((r,i)=>i===idx?{...r,isEditing:!r.isEditing}:r));

  const isSyrupCategory = (med) => {
    if (!med) return false;
    const fields = [med.category, med.cat_name, med.med_category, med.purchase_item_category, med.purchase_item_cat_name, med.type, med.med_type];
    for (const f of fields) {
      if (f && /syrup|syp|liquid|suspension|drops/i.test(String(f))) return true;
    }
    const name = String(med.purchase_item_medicine || med.medicine_name || '').toLowerCase();
    return /\b(syp|syrup|drops|suspension|liquid)\b/.test(name);
  };

  const loadMedicineOptionsForPopup = useCallback(async () => {
    try {
      const fd = new FormData();
      fd.append('page_no', 'none');
      fd.append('search_key', '');
      fd.append('purchase_type', 'normal');
      const res = await axios.post(AppConst.BASE_URL + AppConst.getPurchaseListUrl, fd);
      if (res.data?.status) {
        const recs = res.data.records || [];
        let all = [];
        recs.forEach(r => { if (Array.isArray(r.items)) all = [...all, ...r.items]; });
        const groups = {};
        all.forEach(m => {
          const key = m.purchase_item_medicine;
          if (!groups[key]) groups[key] = { ...m, total_stock: 0 };
          groups[key].total_stock += parseInt(m.purchase_item_qty_stock || 0) + parseInt(m.purchase_item_tab_cap_stock || 0);
        });
        const opts = Object.values(groups).map(m => ({
          value: m.purchase_item_id,
          label: m.purchase_item_medicine,
          stock: m.total_stock,
          composition: m.med_composition || m.med_comp_name || '',
          mrp: m.purchase_item_mrp || '',
          isSyrup: String(m.med_category_name || '').toUpperCase().trim() === 'SYRUP',   // ✅ THIS LINE
        }));
        setMedicineOptions(opts);
      }
    } catch(e) { /* ignore */ }
  }, []);




  const loadTemplatesForPopup = useCallback(async () => {
    try {
      const fd = new FormData();
      fd.append('action', 'search_templates');
      fd.append('search_key', '');
      const res = await axios.post(AppConst.BASE_URL + (AppConst.prescriptionTemplateUrl || 'Webapis/prescription_template'), fd);
      if (res.data?.status && Array.isArray(res.data.records)) {
        setTemplateOptions(res.data.records.map(t => ({
          value: t.template_id,
          label: t.template_name,
          doctor: t.template_doctor_name || '',
        })));
      }
    } catch(e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (show) {
      loadMedicineOptionsForPopup();
      loadTemplatesForPopup();
    }
  }, [show, loadMedicineOptionsForPopup, loadTemplatesForPopup]);

  const openNewMedicine = (ancIdx, geIdx) => {
    setMedPopupAncIdx(ancIdx);
    setMedPopupGeIdx(geIdx);
    setMedPopupMode('new');
    setMedPopupRows([{ sn:1, medicine_name:'', medicine_id:'', morning:'', noon:'', evening:'', night:'', days:'', qty:'', instructions:'' }]);
    setMedPopupTemplate(null);
    setShowMedPopup(true);
  };

  const printGeMedicines = async (ancIdx, geIdx) => {
    const ancRec = records[ancIdx];
    if (!ancRec) { toast.error("ANC record not found"); return; }
    const geRow = ancRec.general_exams?.[geIdx];
    if (!geRow) { toast.error("Exam row not found"); return; }
    if (!geRow.ge_id) { toast.error("Save the exam first"); return; }
  
    try {
      // Fetch medicines saved for this GE row
      const fd = new FormData();
      fd.append('action', 'get_ge_medicines');
      fd.append('ge_id', String(geRow.ge_id));
      const res = await axios.post(AppConst.BASE_URL + (AppConst.ancFileUrl || 'Webapis/anc_file'), fd);
      
      if (!res.data?.status || !Array.isArray(res.data.medicines) || res.data.medicines.length === 0) {
        toast.warning("No medicines found for this exam");
        return;
      }
  
      const meds = res.data.medicines.map(m => ({
        medicine_name: m.opd_medicine_name || '',
        morning: m.opd_medicine_morning || '0',
        noon: m.opd_medicine_noon || '0',
        evening: m.opd_medicine_evening || '0',
        night: m.opd_medicine_night || '0',
        days: m.opd_medicine_days || '',
        qty: m.opd_medicine_qty || '',
        instructions: m.opd_medicine_instructions || '',
        timing: m.opd_medicine_timing || '',
      }));
  
      // Build PDF in OPD format
      await generateAncPrescriptionPdf({
        headerLogo: printHeaderLogo,
        patient: {
          uhid: patient?.opd_uhid || 'N/A',
          name: `${patient?.opd_patient_title||''} ${patient?.opd_patient_name||''}`.trim(),
          address: patient?.opd_address || ancRec.address || 'N/A',
          age: patient?.opd_guardien_age || 'N/A',
          sex: patient?.opd_patient_gender || 'N/A',
          mobile: patient?.opd_patient_mobile || 'N/A',
        },
        bill: {
          opdNo: patient?.opd_id || 'N/A',
          date: new Date().toLocaleString('en-GB',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:true}),
        },
        doctor: {
          name: [ldata?.fname, ldata?.lname].filter(Boolean).join(' ') || 'Doctor',
          degree: ldata?.doc?.doc_degree || '',
          speciality: ldata?.doc?.doc_specality || ldata?.doc?.doc_speciality || '',
        },
        medicines: meds,
        examDate: geRow.date || '',
        ga: geRow.ga || '',
        chief_complain: geRow.chief_complain || '',
        advice: geRow.advice || '',
        investigation: geRow.investigation || '',
        followUp: geRow.follow_up || '',
      });
    } catch(e) {
      console.error('printGeMedicines error:', e);
      toast.error("Failed to print");
    }
  };


  const generateAncPrescriptionPdf = async (data) => {
    const doc = new jsPDF('p', 'pt', 'a4');
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const margin = 40;
  
    // Header logo
    const imageUrlToBase64 = async (url) => {
      if (!url) return null;
      try {
        const r = await fetch(url); if (!r.ok) return null;
        const b = await r.blob();
        return new Promise((res,rej)=>{const rd=new FileReader();rd.onloadend=()=>res(rd.result);rd.onerror=rej;rd.readAsDataURL(b)});
      } catch { return null; }
    };
    const hImg = await imageUrlToBase64(data.headerLogo);
    if (hImg) doc.addImage(hImg, 'PNG', 0, 0, pw, 80);
  
    doc.setFontSize(10).setFont('helvetica','bold').setDrawColor(0).setLineWidth(1.5);
    doc.rect(margin, 90, pw - margin*2, 25);
    doc.text("ANC PRESCRIPTION", pw/2, 107, { align:'center' });
  
    let y = 135;
    const rh = 18, lw = 65;
    const pd = (l,v,x,yy) => {
      doc.setFontSize(9).setFont('helvetica','bold').text(l,x,yy);
      doc.setFont('helvetica','normal').text(`:  ${v||''}`, x+lw, yy);
    };
    const c1 = margin+5, c2 = margin+220, c3 = margin+380;
    pd("UHID No", data.patient.uhid, c1, y);
    pd("OPD No", data.bill.opdNo, c2, y);
    pd("Date", data.bill.date, c3, y);
    y += rh;
    pd("Name", data.patient.name, c1, y);
    pd("Age", data.patient.age, c2, y);
    pd("Sex", data.patient.sex, c3, y);
    y += rh;
    pd("Address", data.patient.address, c1, y);
    pd("Mobile", data.patient.mobile, c2, y);
    y += rh;
    if (data.ga) { pd("GA", data.ga, c1, y); }
    if (data.examDate) { pd("Exam Date", data.examDate, c2, y); }
    y += rh;
    doc.setFont('helvetica','bold').text("Doctor", c1, y);
    doc.setFont('helvetica','normal').text(`:  ${data.doctor.name} ${data.doctor.degree?'('+data.doctor.degree+')':''} ${data.doctor.speciality?'('+data.doctor.speciality+')':''}`, c1+lw, y);
    y += rh;
    doc.setLineWidth(0.8).line(0,y,pw,y);
  
    let mSY = y + 20;
  
    // Chief Complain
    if (data.chief_complain) {
      doc.setFontSize(10).setFont('helvetica','bold').text("Chief Complain:", margin, mSY);
      doc.setFont('helvetica','normal');
      const w = doc.splitTextToSize(data.chief_complain, pw - margin*2 - 100);
      doc.text(w, margin + 100, mSY);
      mSY += w.length * 11 + 8;
    }
  
    // Investigation
    if (data.investigation) {
      doc.setFont('helvetica','bold').text("Investigation:", margin, mSY);
      doc.setFont('helvetica','normal');
      const w = doc.splitTextToSize(data.investigation, pw - margin*2 - 100);
      doc.text(w, margin + 100, mSY);
      mSY += w.length * 11 + 8;
    }
  
    // Big Rx
    if (data.medicines?.length > 0) {
      doc.setFontSize(32).setFont('times','bold').text("Rx", margin, mSY + 22);
      mSY += 32;
  
      const bodyRows = [];
      data.medicines.forEach((m,i) => {
        bodyRows.push([
          { content: `${i+1})`, styles:{ fontStyle:'bold', valign:'top' } },
          { content: `${m.medicine_name}\n(${m.morning||0}-${m.noon||0}-${m.evening||0}-${m.night||0})`, styles:{ fontStyle:'bold' } },
          { content: `${m.instructions?m.instructions+' - ':''}Daily${m.days?' - '+m.days+' Days':' - Till Next Visit'}` },
          { content: m.qty||'', styles:{ halign:'center' } },
        ]);
        if (m.timing && String(m.timing).trim()) {
          bodyRows.push([
            { content:'', styles:{ cellPadding:0 } },
            { content:`Timing:  ${m.timing}`, colSpan:3, styles:{ fontStyle:'italic', fontSize:8, textColor:[60,60,60] } }
          ]);
        }
      });
  
      autoTable(doc, {
        startY: mSY,
        head: [['#', 'Medicine / Dosage', 'Freq - Duration', 'Qty']],
        body: bodyRows,
        theme: 'plain',
        headStyles: { fillColor:[255,255,255], textColor:[0,0,0], fontSize:9, fontStyle:'bold', lineWidth:{bottom:0.5}, lineColor:[0,0,0] },
        bodyStyles: { fontSize:9 },
        columnStyles: { 0:{cellWidth:22}, 1:{cellWidth:220}, 2:{cellWidth:180}, 3:{cellWidth:'auto',halign:'center'} },
        styles: { lineWidth:{bottom:0.3}, lineColor:[180,180,180], cellPadding:3 },
        margin: { left: margin, right: margin },
      });
      mSY = doc.lastAutoTable.finalY + 15;
    }
  
    // Advice
    if (data.advice) {
      doc.setFontSize(10).setFont('helvetica','bold').text("Advice:", margin, mSY);
      doc.setFont('helvetica','normal');
      const w = doc.splitTextToSize(data.advice, pw - margin*2 - 55);
      doc.text(w, margin + 45, mSY);
      mSY += w.length * 11 + 8;
    }
  
    // Follow up
    if (data.followUp) {
      doc.setFontSize(11).setFont('helvetica','bold').text(`Next Visit:  ${data.followUp}`, margin, mSY);
      mSY += 18;
    }
  
    // Footer signature
    const footerY = ph - 60;
    doc.setFontSize(9).setFont('helvetica','normal');
    doc.text("Doctor's Signature: _________________", margin, footerY);
    doc.text(`Printed On: ${new Date().toLocaleString('en-GB')}`, margin + 250, footerY);
  
    window.open(doc.output('bloburl'), '_blank');
  };

  const openOldMedicine = async (ancIdx, geIdx) => {
    setMedPopupAncIdx(ancIdx);
    setMedPopupGeIdx(geIdx);
    setMedPopupMode('old');
    setMedPopupTemplate(null);
    setMedPopupLoading(true);
    setShowMedPopup(true);
    try {
      const fd = new FormData();
      fd.append('action', 'get_last_prescription');
      fd.append('uhid', patient?.opd_uhid || '');
      const res = await axios.post(AppConst.BASE_URL + (AppConst.ancFileUrl || 'Webapis/anc_file'), fd);
      if (res.data?.status && Array.isArray(res.data.medicines) && res.data.medicines.length > 0) {
        setMedPopupRows(res.data.medicines.map((m, i) => ({
          sn: i + 1,
          medicine_name: m.opd_medicine_name || '',
          medicine_id: m.opd_medicine_id_ref || '',
          morning: m.opd_medicine_morning || '',
          noon: m.opd_medicine_noon || '',
          evening: m.opd_medicine_evening || '',
          night: m.opd_medicine_night || '',
          days: m.opd_medicine_days || '',
          qty: m.opd_medicine_qty || '',
          instructions: m.opd_medicine_instructions || '',
          _lastDate: res.data.opd_datetime || '',
        })));
      } else {
        toast.info("No previous prescription found");
        setMedPopupRows([]);
      }
    } catch (e) { toast.error("Error loading previous prescription"); }
    finally { setMedPopupLoading(false); }
  };

  // ─── FIXED: Template application — now correctly fetches full template ───
  const applyTemplateToPopup = async (tpl) => {
    if (!tpl) { setMedPopupTemplate(null); return; }
    setMedPopupTemplate(tpl);
    setMedPopupLoading(true);
    try {
      // Fetch FULL template (search_templates returns only metadata)
      const fd = new FormData();
      fd.append('action', 'get_template');
      fd.append('template_id', String(tpl.value));
      const res = await axios.post(AppConst.BASE_URL + (AppConst.prescriptionTemplateUrl || 'Webapis/prescription_template'), fd);
      if (res.data?.status && res.data.record) {
        const t = res.data.record;
        const meds = Array.isArray(t.medicines) ? t.medicines : [];
        if (meds.length === 0) {
          toast.warning(`Template "${tpl.label}" has no medicines`);
          setMedPopupLoading(false);
          return;
        }
        const rows = meds.map((m, i) => ({
          sn: i+1,
          medicine_name: m.medicine_name || '',
          medicine_id: m.medicine_id || '',
          morning: m.morning || '',
          noon: m.noon || '',
          evening: m.evening || '',
          night: m.night || '',
          days: m.days || '',
          qty: m.qty || '',
          instructions: m.instructions || m.notes || '',
        }));
        setMedPopupRows(rows);
        toast.success(`Template "${tpl.label}" applied — ${meds.length} medicines loaded`);
      } else {
        toast.error("Failed to load template");
      }
    } catch(e) {
      console.error("applyTemplateToPopup error:", e);
      toast.error("Error loading template");
    } finally {
      setMedPopupLoading(false);
    }
  };

  const medPopupUpd = (idx, field, value) => {
    setMedPopupRows(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const u = { ...r, [field]: value };
      if (['morning','noon','evening','night','days'].includes(field)) {
        if (u.isSyrup) {
          u.qty = '1';   // ← syrup always 1
        } else {
          const daily = parseFloat(u.morning||0)+parseFloat(u.noon||0)+parseFloat(u.evening||0)+parseFloat(u.night||0);
          u.qty = (daily * parseInt(u.days||0)) > 0 ? (daily * parseInt(u.days||0)).toFixed(0) : '';
        }
      }
      return u;
    }));
  };



  const medPopupAdd = () => setMedPopupRows(prev => [...prev, { sn: prev.length+1, medicine_name:'', medicine_id:'', morning:'', noon:'', evening:'', night:'', days:'', qty:'', instructions:'' }]);
  const medPopupRem = (idx) => setMedPopupRows(prev => prev.filter((_, i) => i !== idx));

  const saveMedPopup = async () => {
    if (medPopupMode === 'old') {
      toast.info("Old prescription is read-only. Use 'New Medicine' to add fresh entries.");
      return;
    }
    const ancRec = records[medPopupAncIdx];
    if (!ancRec) { toast.error("ANC record not found"); return; }
    const geRows = ancRec.general_exams || [];
    let geRow = geRows[medPopupGeIdx];
    if (!geRow) { toast.error("General Exam row not found"); return; }
    if (!geRow.ge_id) {
      const newGeId = `ge_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      const updatedGeRows = geRows.map((g, i) => i === medPopupGeIdx ? { ...g, ge_id: newGeId } : g);
      updateField(medPopupAncIdx, 'general_exams', updatedGeRows);
      geRow = { ...geRow, ge_id: newGeId };
    }
    if (!patient?.opd_id) { toast.error("OPD ID missing"); return; }
    const validRows = medPopupRows.filter(r => r.medicine_name?.trim());
    if (validRows.length === 0) { toast.error("Add at least one medicine"); return; }
    try {
      const now = new Date();
      const pad = n => String(n).padStart(2, '0');
      const group_date = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}`;
      const group_time = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
      const group_batch_id = `ancbatch_${now.getTime()}`;
      const group_note = `ANC - GE dated ${geRow.date || group_date}`;
      const fd = new FormData();
      fd.append('action', 'save_ge_medicines');
      fd.append('opd_id', String(patient.opd_id));
      fd.append('anc_id', String(ancRec.anc_id || '0'));
      fd.append('ge_id', String(geRow.ge_id));
      fd.append('user_id', String(ldata?.user_id || 1));
      fd.append('source', 'ANC_NEW');
      fd.append('group_date', group_date);
      fd.append('group_time', group_time);
      fd.append('group_note', group_note);
      fd.append('group_batch_id', group_batch_id);
      fd.append('medicines', JSON.stringify(validRows.map(r => ({
        medicine_name: r.medicine_name, medicine_id: r.medicine_id || '',
        morning: r.morning || '0', noon: r.noon || '0', evening: r.evening || '0',
        night: r.night || '0', days: r.days || '0', qty: r.qty || '0',
        instructions: r.instructions || '', timing: r.timing || '',
      }))));
      const url = AppConst.BASE_URL + (AppConst.ancFileUrl || 'Webapis/anc_file');
      const res = await axios.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.status) {
        toast.success(`✓ ${validRows.length} medicine(s) saved`);
        window._ancMedsSaved = true;
        setShowMedPopup(false);
        if (!ancRec.anc_id) toast.info("Remember to save the ANC record.");
      } else toast.error(res.data?.message || "Backend rejected save");
    } catch (e) { toast.error(`Error: ${e.message || 'Network'}`); }
  };

  const openCloseModal = (idx) => { setCloseTargetIdx(idx); setCloseReason(''); setShowCloseModal(true); };

  const confirmCloseAnc = async () => {
    if (!closeReason.trim()) { toast.error("Please enter a reason"); return; }
    const rec = records[closeTargetIdx];
    if (!rec?.anc_id) { toast.error("Save first"); return; }
    try {
      const fd = new FormData();
      fd.append('action', 'close_anc');
      fd.append('anc_id', String(rec.anc_id));
      fd.append('close_reason', closeReason.trim());
      fd.append('closed_by', [ldata?.fname, ldata?.lname].filter(Boolean).join(' ') || ldata?.user_id || 'Unknown');
      const res = await axios.post(AppConst.BASE_URL + (AppConst.ancFileUrl || "Webapis/anc_file"), fd, { headers:{'Content-Type':'multipart/form-data'} });
      if (res.data?.status) { toast.success("Closed"); setShowCloseModal(false); setCloseTargetIdx(null); setCloseReason(''); await fetchAncRecords(); }
      else toast.error(res.data?.message || "Failed");
    } catch(e) { toast.error("Error"); }
  };

  const reopenAnc = async (idx) => {
    const rec = records[idx];
    if (!rec?.anc_id) return;
    const r = await Swal.fire({ title: "Reopen?", icon: "question", showCancelButton: true, confirmButtonText: "Reopen", customClass:{container:'swal-above-modal'} });
    if (!r.isConfirmed) return;
    try {
      const fd = new FormData();
      fd.append('action', 'reopen_anc');
      fd.append('anc_id', String(rec.anc_id));
      const res = await axios.post(AppConst.BASE_URL + (AppConst.ancFileUrl || "Webapis/anc_file"), fd);
      if (res.data?.status) { toast.success("Reopened"); await fetchAncRecords(); }
      else toast.error("Failed");
    } catch(e) { toast.error("Error"); }
  };

  const openPrintModal = (idx) => {
    setPrintTargetIdx(idx);
    setPrintSections({ basic:true, menstrual:true, obstetric:true, investigation:true, immunization:true, general_exam:true, past_history:true, family_history:true, personal_history:true });
    setShowPrintModal(true);
  };
  const togglePrintSection = (key) => setPrintSections(p => ({ ...p, [key]: !p[key] }));
  const toggleAllPrintSections = (val) => {
    const next = {};
    ['basic','menstrual','obstetric','investigation','immunization','general_exam','past_history','family_history','personal_history'].forEach(k => { next[k] = val; });
    setPrintSections(next);
  };

  const doPrint = () => {
    const rec = records[printTargetIdx];
    if (!rec) return;
    const selected = Object.keys(printSections).filter(k => printSections[k]);
    if (selected.length === 0) { toast.error("Select at least one section"); return; }
    const doctorName  = [ldata?.fname, ldata?.lname].filter(Boolean).join(' ') || ldata?.name || 'Doctor';
    const doctorDegree = ldata?.doc?.doc_degree || ldata?.doc_degree || '';
    const doctorSpec  = ldata?.doc?.doc_specality || ldata?.doc?.doc_speciality || '';
    const doctorRegNo = ldata?.doc?.doc_reg_number || ldata?.doc?.doc_reg_no || '';
    const html = buildPrintHtml(rec, selected, patient, { headerLogo: printHeaderLogo, doctorName, doctorDegree, doctorSpec, doctorRegNo });
    const win = window.open('', '_blank');
    if (!win) { toast.error("Popup blocked"); return; }
    win.document.write(html);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); };
    setShowPrintModal(false);
  };

  /* ════════════════════════════════════════════════
     PRINT HTML — UPDATED LAYOUT
     - Patient (left) | Husband (right) side by side
     - Pregnancy Summary + Delivery Note side by side
     - Past History + Family History side by side
     ════════════════════════════════════════════════ */
  const buildPrintHtml = (rec, sections, patient, opts = {}) => {
    const { headerLogo = '', doctorName = '', doctorDegree = '', doctorSpec = '', doctorRegNo = '' } = opts;
    const pName = `${patient?.opd_patient_title||''} ${patient?.opd_patient_name||''}`.trim();
    const safe = (v) => (v === null || v === undefined || v === '') ? '-' : String(v);

    const now = new Date();
    const dd = String(now.getDate()).padStart(2,'0');
    const mm = String(now.getMonth()+1).padStart(2,'0');
    const yyyy = now.getFullYear();
    let hh = now.getHours();
    const mi = String(now.getMinutes()).padStart(2,'0');
    const ampm = hh >= 12 ? 'PM' : 'AM';
    hh = hh % 12 || 12;
    const printDate = `${dd}-${mm}-${yyyy}`;
    const printDateTime = `${printDate} ${String(hh).padStart(2,'0')}:${mi} ${ampm}`;

    const css = `
    <style>
      @page { size: A4; margin: 15mm 12mm 20mm 12mm; }
      body { font-family: Arial, sans-serif; color:#000; font-size:11px; margin:0; padding:0; }
      .page-wrap { padding: 0; }
      .hosp-header { width:100%; margin-bottom:8px; }
      .hosp-header img { width:100%; display:block; }
      .hosp-header-text { text-align:center; padding:8px 0; border-bottom:2px solid #405189; }
      .hosp-header-text h1 { margin:0; color:#405189; font-size:20px; }
      .report-title { text-align:center; padding:5px; background:#e8eef6; border:1px solid #405189; margin:6px 0; }
      .report-title h2 { margin:0; color:#405189; font-size:14px; }
      .print-meta { font-size:9px; color:#666; text-align:right; margin-bottom:4px; }
      h2 { font-size:13px; margin:10px 0 5px; padding:3px 6px; background:#e8eef6; color:#405189; border-left:3px solid #405189; }
      h3 { font-size:11px; margin:6px 0 3px; padding:2px 4px; background:#f1f5fb; color:#405189; font-weight:700; }
      .meta { display:flex; justify-content:space-between; border:1px solid #c7d2fe; padding:6px; margin-bottom:8px; font-size:10px; flex-wrap:wrap; gap:4px; }
      table { width:100%; border-collapse:collapse; margin-bottom:6px; }
      th, td { border:1px solid #999; padding:3px 5px; font-size:10px; text-align:left; vertical-align:top; }
      th { background:#f1f5fb; font-weight:700; }
      .kv { display:grid; grid-template-columns: 110px 1fr; gap:4px; margin-bottom:2px; font-size:10px; }
      .kv .k { font-weight:700; color:#405189; }
      .card-sec { border:1px solid #c7d2fe; padding:6px; margin-bottom:6px; border-radius:3px; }
      .risk { display:inline-block; padding:1px 6px; background:#dc3545; color:#fff; font-weight:700; border-radius:2px; font-size:9px; }
      .normal { display:inline-block; padding:1px 6px; background:#198754; color:#fff; font-weight:700; border-radius:2px; font-size:9px; }
      .closed { background:#fef3c7; border:2px solid #f59e0b; padding:6px; margin-bottom:8px; border-radius:3px; font-size:10px; }
      .row-2col { display:flex; gap:8px; margin-bottom:6px; }
      .row-2col > div { flex:1; min-width:0; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; page-break-after: auto; }
      thead { display: table-header-group; }
      .card-sec, h2 { page-break-inside: avoid; }
      .signature-block { margin-top:25px; padding-top:10px; page-break-inside:avoid; }
      .sig-row { display:flex; justify-content:space-between; align-items:flex-end; }
      .sig-col { width:45%; }
      .sig-line { border-top:1.5px solid #000; padding-top:3px; margin-top:40px; text-align:center; }
      .sig-name { font-weight:700; font-size:12px; }
      .sig-meta { font-size:9px; color:#555; margin-top:2px; }
      .sig-date { text-align:right; font-size:10px; }
      @media print { .page-wrap { padding: 0; } }
    </style>`;

    let header = '';
    if (headerLogo) {
      header = `<div class="hosp-header"><img src="${headerLogo}" alt="Hospital Header"/></div>`;
    } else {
      header = `<div class="hosp-header-text"><h1>ANC File Report</h1></div>`;
    }

    let body = header;
    body += `<div class="print-meta">Printed: ${printDateTime}</div>`;
    body += `<div class="report-title"><h2>ANC FILE REPORT</h2></div>`;
    body += `<div class="meta">
      <div><b>Patient:</b> ${safe(pName)}</div>
      <div><b>UHID:</b> ${safe(patient?.opd_uhid)}</div>
      <div><b>Age/Gender:</b> ${safe(patient?.opd_guardien_age)} / ${safe(patient?.opd_patient_gender)}</div>
      <div><b>Mobile:</b> ${safe(patient?.opd_patient_mobile)}</div>
    </div>`;

    if (rec.is_closed) {
      body += `<div class="closed"><b>⚠ FILE CLOSED</b> — Reason: ${safe(rec.close_reason)} | Closed by: ${safe(rec.closed_by)} | On: ${safe(rec.closed_at)}</div>`;
    }

    // ─── BASIC INFO ───
    if (sections.includes('basic')) {
      // PATIENT (left) | HUSBAND (right) side by side
      body += `<h2>Basic Info</h2>`;
      body += `<div class="row-2col">
        <div class="card-sec">
          <h3>👤 Patient</h3>
          <div class="kv"><span class="k">Name:</span><span>${safe(pName)}</span></div>
          <div class="kv"><span class="k">Occupation:</span><span>${safe(rec.occupation)}</span></div>
          <div class="kv"><span class="k">Marriage:</span><span>${safe(rec.marriage)}</span></div>
          <div class="kv"><span class="k">Blood Group:</span><span>${safe(rec.blood_group)}</span></div>
          <div class="kv"><span class="k">Address:</span><span>${safe(rec.address)}</span></div>
          <div class="kv"><span class="k">Consanguinity:</span><span>${safe(rec.consanguinity)}</span></div>
        </div>
        <div class="card-sec">
          <h3>👥 Husband</h3>
          <div class="kv"><span class="k">Name:</span><span>${safe(rec.husband_name)}</span></div>
          <div class="kv"><span class="k">Age:</span><span>${safe(rec.husband_age)}</span></div>
          <div class="kv"><span class="k">Occupation:</span><span>${safe(rec.husband_occupation)}</span></div>
          <div class="kv"><span class="k">Marriage:</span><span>${safe(rec.husband_marriage)}</span></div>
          <div class="kv"><span class="k">Blood Group:</span><span>${safe(rec.husband_blood_group)}</span></div>
          <div class="kv"><span class="k">Address:</span><span>${safe(rec.husband_address)}</span></div>
        </div>
      </div>`;

      // PREGNANCY SUMMARY (left) | DELIVERY NOTE (right) side by side
      const dn = rec.delivery_note || {};
      body += `<div class="row-2col">
        <div class="card-sec">
          <h3>🤰 Pregnancy Summary</h3>
          <div class="kv"><span class="k">GPLA:</span><span style="font-weight:700;font-size:13px">${safe(formatGplaSubscript(rec.gpla_details))}</span></div>
          <div class="kv"><span class="k">GA:</span><span>${safe(rec.ga || calculateGA(rec.c_lmp||rec.lmp))}</span></div>
          <div class="kv"><span class="k">Mode:</span><span>${safe(rec.mode)}</span></div>
          <div class="kv"><span class="k">Chief Complain:</span><span>${safe(rec.chief_complain)}</span></div>
          <div class="kv"><span class="k">Risk Status:</span><span class="${rec.risk_status==='Risk'?'risk':'normal'}">${safe(rec.risk_status)}</span></div>
          <div class="kv"><span class="k">LMP:</span><span>${safe(rec.lmp)}</span></div>
          <div class="kv"><span class="k">EDD:</span><span>${safe(rec.edd)}</span></div>
          <div class="kv"><span class="k">C-LMP:</span><span>${safe(rec.c_lmp)}</span></div>
          <div class="kv"><span class="k">C-EDD:</span><span>${safe(rec.c_edd)}</span></div>
          ${rec.risk_status==='Risk' ? `<div class="kv"><span class="k">Risk Reasons:</span><span>${(rec.high_risk_reasons||[]).join(', ') || '-'}</span></div>
          <div class="kv"><span class="k">Risk Remarks:</span><span>${safe(rec.high_risk_remarks)}</span></div>` : ''}
          <div class="kv"><span class="k">Remarks:</span><span>${safe(rec.remarks)}</span></div>
        </div>
        <div class="card-sec">
          <h3>📋 Delivery Note</h3>
          <div class="kv"><span class="k">Date:</span><span>${safe(dn.date)} ${safe(dn.time)}</span></div>
          <div class="kv"><span class="k">Sex / Baby Wt:</span><span>${safe(dn.sex)} / ${safe(dn.baby_wt)} kg</span></div>
          <div class="kv"><span class="k">Hospital:</span><span>${safe(dn.hospital_name)}</span></div>
          <div class="kv"><span class="k">Term:</span><span>${safe(dn.full_pre_term)}</span></div>
          <div class="kv"><span class="k">Mode:</span><span>${safe(dn.mode)}</span></div>
          <div class="kv"><span class="k">Outcome:</span><span>${safe(dn.outcome)}</span></div>
          <div class="kv"><span class="k">Complication:</span><span>${safe(dn.complication)}</span></div>
          <div class="kv"><span class="k">Remarks:</span><span>${safe(dn.remarks)}</span></div>
        </div>
      </div>`;
    }


    const printResult = (val) => {
      if (!val) return '-';
      const v = String(val).trim().toLowerCase();
      if (v === 'positive') return `<span style="background:#d1fae5;color:#065f46;border:1px solid #10b981;padding:1px 6px;border-radius:3px;font-weight:700">${val}</span>`;
      if (v === 'negative') return `<span style="background:#fee2e2;color:#991b1b;border:1px solid #ef4444;padding:1px 6px;border-radius:3px;font-weight:700">${val}</span>`;
      return val;
    };
    // ─── MENSTRUAL ───
    if (sections.includes('menstrual')) {
      const m = rec.menstrual_history || {};
      body += `<h2>Menstrual History</h2><table>
        <tr><th>Menarche Age</th><td>${safe(m.menarche_age)}</td><th>Cycle</th><td>${safe(m.cycle)}</td><th>Duration</th><td>${safe(m.duration)}</td></tr>
        <tr><th>Frequency</th><td>${safe(m.frequency)}</td><th>Flow</th><td>${safe(m.flow)}</td><th>Pain</th><td>${safe(m.pain)}</td></tr>
        <tr><th>LMP</th><td>${safe(m.lmp)}</td><th>EDD</th><td colspan="3">${safe(m.edd)}</td></tr>
      </table>`;
    }

    // ─── OBSTETRIC ───
    if (sections.includes('obstetric')) {
      body += `<h2>Obstetric History</h2><table>
        <tr><th>#</th><th>Abortion</th><th>Sex</th><th>Age</th><th>Term</th><th>Mode</th><th>Baby Wt</th><th>Outcome</th><th>Complication</th><th>Remarks</th></tr>`;
      (rec.obstetric_history||[]).forEach((ob,i) => {
        body += `<tr><td>G${i+1}</td><td>${safe(ob.abortion)}</td><td>${safe(ob.sex)}</td><td>${safe(ob.age_years)}</td><td>${safe(ob.full_pre_term)}</td><td>${safe(ob.mode)}</td><td>${safe(ob.baby_weight)}</td><td>${safe(ob.outcome)}</td><td>${safe(ob.complication)}</td><td>${safe(ob.remarks)}</td></tr>`;
      });
      body += `</table>`;
    }

    // ─── INVESTIGATION ───
    if (sections.includes('investigation')) {
      body += `<h2>Investigation</h2>`;
      const fixedRows = (rec.investigations||[]).filter(r => r.type === 'fixed');
      const leftFixed = fixedRows.filter(r => FIXED_TESTS_LEFT.includes(r.test_name));
      const rightFixed = fixedRows.filter(r => FIXED_TESTS_RIGHT.includes(r.test_name));
      const datedRows = (rec.investigations||[]).filter(r => r.type === 'dated');

      if (leftFixed.length > 0) {
        body += `<h3>Done-only Tests</h3><table><tr><th>Test</th><th>Done Date</th><th>Result</th></tr>`;
        FIXED_TESTS_LEFT.forEach(t => {
          const r = leftFixed.find(x => x.test_name === t) || {};
          body += `<tr><td><b>${t}</b></td><td>${safe(r.done_date)}</td><td>${printResult(r.result)}</td></tr>`;
        });
        body += `</table>`;
      }
      if (rightFixed.length > 0) {
        body += `<h3>Scheduled Tests</h3><table><tr><th>Test</th><th>Given Date</th><th>Due From</th><th>Due To</th><th>Done Date</th><th>Result</th></tr>`;
        FIXED_TESTS_RIGHT.forEach(t => {
          const r = rightFixed.find(x => x.test_name === t) || {};
          body += `<tr><td><b>${t}</b></td><td>${safe(r.given_date)}</td><td>${safe(r.due_date_from)}</td><td>${safe(r.due_date_to)}</td><td>${safe(r.done_date)}</td><td>${printResult(r.result)}</td></tr>`;
        });
        body += `</table>`;
      }
      if (datedRows.length > 0) {
        body += `<h3>Dated Lab Tests</h3><table><tr><th>DATE</th>${DATED_COLUMNS.map(c=>`<th>${c}</th>`).join('')}</tr>`;
        datedRows.forEach(r => {
          body += `<tr><td>${safe(r.date)}</td>${DATED_COLUMNS.map(c=>`<td>${safe(r[c])}</td>`).join('')}</tr>`;
        });
        body += `</table>`;
      }
    }

    // ─── IMMUNIZATION ───
    if (sections.includes('immunization')) {
      body += `<h2>Immunization</h2><table>
        <tr><th>#</th><th>Name</th><th>Given Date</th><th>Due From</th><th>Due To</th><th>Done Date</th><th>Result</th><th>Remarks</th></tr>`;
      (rec.immunizations||[]).forEach((im,i) => {
        body += `<tr><td>${i+1}</td><td>${safe(im.name)}</td><td>${safe(im.given_date)}</td><td>${safe(im.due_date_from)}</td><td>${safe(im.due_date_to)}</td><td>${safe(im.done_date)}</td><td>${safe(im.result)}</td><td>${safe(im.remarks)}</td></tr>`;
      });
      if ((rec.immunizations||[]).length === 0) body += `<tr><td colspan="8">No immunization records.</td></tr>`;
      body += `</table>`;
    }

    // ─── GENERAL EXAM ───
    if (sections.includes('general_exam')) {
      body += `<h2>General Examination</h2><table style="font-size:9px">
        <tr><th>#</th><th>Date</th><th>GA</th><th>Wt</th><th>Pallor</th><th>Oedema</th><th>BP</th><th>Pulse</th><th>SPO2</th><th>Temp</th><th>FHR</th><th>USG</th><th>PA</th><th>PV</th><th>Chief Comp.</th><th>Advice</th><th>Investigation</th><th>Remarks</th><th>F/U</th></tr>`;
      (rec.general_exams||[]).forEach((g,i) => {
        body += `<tr><td>${i+1}</td><td>${safe(g.date)}</td><td>${safe(g.ga)}</td><td>${safe(g.weight)}</td><td>${safe(g.pallor)}</td><td>${safe(g.oedema)}</td><td>${safe(g.bp)}</td><td>${safe(g.pulse)}</td><td>${safe(g.spo2)}</td><td>${safe(g.temp)}</td><td>${safe(g.fhr)}</td><td>${safe(g.usg)}</td><td>${safe(g.pa)}</td><td>${safe(g.pv)}</td><td>${safe(g.chief_complain)}</td><td>${safe(g.advice)}</td><td>${safe(g.investigation)}</td><td>${safe(g.remarks)}</td><td>${safe(g.follow_up)}</td></tr>`;
      });
      if ((rec.general_exams||[]).length === 0) body += `<tr><td colspan="19">No examinations recorded.</td></tr>`;
      body += `</table>`;
    }

    // ─── PAST + FAMILY HISTORY SIDE BY SIDE ───
    const renderKvTable = (obj, types) => {
      let h = `<table>`;
      types.forEach(t => { h += `<tr><th style="width:130px">${t}</th><td>${safe(obj[t])}</td></tr>`; });
      return h + `</table>`;
    };

    if (sections.includes('past_history') && sections.includes('family_history')) {
      body += `<h2>Past & Family History</h2>`;
      body += `<div class="row-2col">
        <div><h3>Past History</h3>${renderKvTable(rec.past_history||{}, PAST_HISTORY_TYPES)}</div>
        <div><h3>Family History</h3>${renderKvTable(rec.family_history||{}, FAMILY_HISTORY_TYPES)}</div>
      </div>`;
    } else if (sections.includes('past_history')) {
      body += `<h2>Past History</h2>${renderKvTable(rec.past_history||{}, PAST_HISTORY_TYPES)}`;
    } else if (sections.includes('family_history')) {
      body += `<h2>Family History</h2>${renderKvTable(rec.family_history||{}, FAMILY_HISTORY_TYPES)}`;
    }

    if (sections.includes('personal_history')) {
      body += `<h2>Personal History</h2>${renderKvTable(rec.personal_history||{}, PERSONAL_HISTORY_TYPES)}`;
    }

    // ─── SIGNATURE ───
    const signatureBlock = `
    <div class="signature-block">
      <div class="sig-row">
        <div class="sig-col sig-date">
          <div><b>Date:</b> ${printDate}</div>
          <div style="margin-top:3px"><b>Time:</b> ${String(hh).padStart(2,'0')}:${mi} ${ampm}</div>
        </div>
        <div class="sig-col">
          <div class="sig-line">
            <div class="sig-name">${safe(doctorName)}${doctorDegree ? ' (' + doctorDegree + ')' : ''}</div>
            <div class="sig-meta">${doctorSpec || ''}${doctorRegNo ? ' | Reg No: ' + doctorRegNo : ''}</div>
            <div class="sig-meta" style="margin-top:2px"><b>Doctor's Signature</b></div>
          </div>
        </div>
      </div>
    </div>`;

    return `<!DOCTYPE html><html><head><title>ANC File - ${safe(pName)}</title>${css}</head>
    <body><div class="page-wrap">${body}${signatureBlock}</div></body></html>`;
  };

  const formatTs = (dt) => { if(!dt)return''; try{const d=new Date(dt);if(isNaN(d.getTime()))return dt;let h=d.getHours();const ap=h>=12?'PM':'AM';h=h%12||12;return`${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()} ${String(h).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')} ${ap}`;}catch{return dt;} };
  const getRiskStyle = s => s==='Risk'?{backgroundColor:'#dc3545',color:'#fff',fontWeight:700,padding:'4px 12px',borderRadius:4}:{backgroundColor:'#198754',color:'#fff',fontWeight:600,padding:'4px 12px',borderRadius:4};
  const patientName=`${patient?.opd_patient_title||''} ${patient?.opd_patient_name||''}`.trim();

  const sectionTabs = [
    {key:'basic', label:'Basic Info', icon:'ri-user-line'},
    {key:'menstrual', label:'Menstrual History', icon:'ri-calendar-2-line'},
    {key:'obstetric', label:'Obstetric History', icon:'ri-stethoscope-line'},
    {key:'investigation', label:'Investigation', icon:'ri-test-tube-line'},
    //{key:'immunization', label:'Immunization', icon:'ri-syringe-line'},
    {key:'general_exam', label:'General Exam', icon:'ri-heart-pulse-line'},
    {key:'past_history', label:'Past History', icon:'ri-history-line'},
    {key:'family_history', label:'Family History', icon:'ri-group-line'},
    {key:'personal_history', label:'Personal History', icon:'ri-user-heart-line'},
  ];

  return (
    <>
      <Modal show={show} onHide={onHide} fullscreen={true} style={{zIndex:99998}} enforceFocus={false} dialogClassName="anc-fullscreen-modal">
        <Modal.Header closeButton style={{backgroundColor:'#f0f4ff',borderBottom:'2px solid #c7d2fe'}}>
          <Modal.Title style={{color:'#405189'}}><i className="ri-file-text-line me-2"></i>ANC File — {patientName||'Patient'}</Modal.Title>
          <div className="ms-auto me-3"><button className="btn btn-primary btn-sm" onClick={addNewRecord}><i className="ri-add-line me-1"></i>Add New Record</button></div>
        </Modal.Header>

        <style>{`
          .anc-fullscreen-modal { width: 100vw !important; max-width: 100vw !important; height: 100vh !important; margin: 0 !important; }
          .anc-fullscreen-modal .modal-content { height: 100vh !important; border-radius: 0 !important; }
          .anc-fullscreen-modal .modal-body { max-height: calc(100vh - 120px) !important; overflow-y: auto !important; }
        `}</style>

        <Modal.Body style={{padding:20,backgroundColor:'#f8f9fa',overflowY:'auto'}}>
          {/* Patient Header */}
          <div className="card mb-3" style={{border:'1px solid #c7d2fe'}}>
            <div className="card-body py-2">
              <div className="row align-items-center">
                {[{l:'Patient Name',v:patientName},{l:'Age',v:patient?.opd_guardien_age||'N/A'},{l:'UHID',v:patient?.opd_uhid||'N/A'},{l:'Gender',v:patient?.opd_patient_gender||'N/A'},{l:'Mobile',v:patient?.opd_patient_mobile||'N/A'}].map((item,i)=>(
                  <div className={i<2?"col-md-3":"col-md-2"} key={i}><small className="text-muted">{item.l}</small><div className="fw-bold" style={{fontSize:i<2?16:14,color:i<2?'#405189':'inherit'}}>{item.v||'N/A'}</div></div>
                ))}
              </div>
            </div>
          </div>

          {loading && <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>}
          {!loading && records.length===0 && <div className="text-center py-5"><i className="ri-file-add-line" style={{fontSize:48,color:'#d1d5db'}}></i><p className="text-muted mt-2">No ANC records. Click "Add New Record".</p></div>}

          {records.map((rec,idx) => {
            const recKey=rec.anc_id||`new_${idx}`;
            const isExp=expandedId===recKey;
            const isEd=rec.isEditing;

            return (
              <div key={recKey} style={{border:`1px solid ${isEd?'#818cf8':'#e5e7eb'}`,borderRadius:8,marginBottom:12,overflow:'hidden',boxShadow:isEd?'0 0 0 2px rgba(99,102,241,0.2)':'none'}}>
                <div onClick={()=>setExpandedId(isExp?null:recKey)} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 16px',cursor:'pointer',background:isExp?'#eef2ff':rec.isNew?'#fff7ed':'#fff'}}>
                  <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                    <strong style={{color:'#405189'}}>{rec.isNew?'📝 New':`#${rec.anc_id}`}</strong>
                    {rec.created_at && <span className="text-muted" style={{fontSize:13}}><i className="ri-time-line me-1"></i>{formatTs(rec.created_at)}</span>}
                    {rec.gpla && <span className="badge bg-primary" style={{fontSize:12}}>{rec.gpla}</span>}
                    <span style={getRiskStyle(rec.risk_status)}>{rec.risk_status}</span>
                    {rec.chief_complain && <span className="text-muted" style={{fontSize:13}}>CC: {rec.chief_complain}</span>}
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    {!rec.isNew && (
                      <>
                        {rec.is_closed && <span className="badge bg-warning text-dark me-1" style={{fontSize:11}}><i className="ri-lock-2-line me-1"></i>CLOSED</span>}
                        <button className="btn btn-sm btn-outline-info" onClick={e=>{e.stopPropagation();openPrintModal(idx);}} title="Print"><i className="ri-printer-line"></i></button>
                        <button className="btn btn-sm btn-outline-warning" onClick={e=>{e.stopPropagation();if(rec.is_closed && !isAdmin){toast.warning("File closed. Admin only.");return;}toggleEdit(idx);setExpandedId(recKey);}} disabled={rec.is_closed && !isAdmin}><i className="ri-pencil-line"></i></button>
                        {!rec.is_closed ? (
                          <button className="btn btn-sm btn-outline-secondary" onClick={e=>{e.stopPropagation();openCloseModal(idx);}} title="Close File"><i className="ri-lock-line"></i></button>
                        ) : isAdmin ? (
                          <button className="btn btn-sm btn-outline-success" onClick={e=>{e.stopPropagation();reopenAnc(idx);}} title="Reopen"><i className="ri-lock-unlock-line"></i></button>
                        ) : null}
                        <button className="btn btn-sm btn-outline-danger" onClick={e=>{e.stopPropagation();if(rec.is_closed && !isAdmin){toast.warning("Admin only");return;}deleteRecord(idx);}} disabled={rec.is_closed && !isAdmin}><i className="ri-delete-bin-line"></i></button>
                      </>
                    )}
                    <span style={{fontSize:18,color:'#9ca3af'}}>{isExp?'▲':'▼'}</span>
                  </div>
                </div>
                {rec.is_closed && (
                  <div style={{padding:'10px 16px', background:'#fef3c7', borderBottom:'2px solid #f59e0b', fontSize:13}}>
                    <i className="ri-lock-2-line me-2" style={{color:'#d97706'}}></i>
                    <strong style={{color:'#92400e'}}>This file is closed.</strong>
                    <span className="ms-2">Reason: <em>{rec.close_reason || '—'}</em></span>
                    {!isAdmin && <span className="ms-2 text-danger"><i className="ri-error-warning-line me-1"></i>Read-only</span>}
                  </div>
                )}

                {isExp && (
                  <div style={{background:'#f9fafb'}}>
                    <div style={{padding:'8px 16px',borderBottom:'1px solid #e5e7eb',overflowX:'auto',whiteSpace:'nowrap',background:'#fff'}}>
                      {sectionTabs.map(tab=>(
                        <button key={tab.key} className={`btn btn-sm me-1 ${activeSection===tab.key?'btn-primary':'btn-outline-secondary'}`} style={{fontSize:11,padding:'4px 10px'}} onClick={()=>setActiveSection(tab.key)}>
                          <i className={`${tab.icon} me-1`}></i>{tab.label}
                        </button>
                      ))}
                    </div>

                    {activeSection === 'basic' && (
                      <div style={{padding:20, background:'#fff', borderBottom:'1px solid #e5e7eb'}}>
                        <h6 style={{color:'#405189', fontWeight:700, marginBottom:12}}><i className="ri-user-line me-2"></i>Basic Info</h6>

                        <div className="card mb-3" style={{border:'1px solid #c7d2fe'}}>
                          <div className="card-header py-2" style={{background:'#eef2ff'}}>
                            <h6 className="mb-0" style={{color:'#405189',fontSize:13}}><i className="ri-user-3-line me-2"></i>Patient</h6>
                          </div>
                          <div className="card-body py-2">
                            <div className="row g-2">
                              <div className="col-md-3"><label className="form-label fw-bold" style={{fontSize:12}}>Name</label><div className="form-control-plaintext" style={{fontSize:13}}>{patientName||'N/A'}</div></div>
                              <div className="col-md-1"><label className="form-label fw-bold" style={{fontSize:12}}>Age</label><div className="form-control-plaintext" style={{fontSize:13}}>{patient?.opd_guardien_age||'N/A'}</div></div>
                              <div className="col-md-2"><label className="form-label fw-bold" style={{fontSize:12}}>Occupation</label>{isEd?<input className="form-control form-control-sm" value={rec.occupation} onChange={e=>updateField(idx,'occupation',e.target.value)}/>:<div className="form-control-plaintext" style={{fontSize:13}}>{rec.occupation||'N/A'}</div>}</div>
                              <div className="col-md-2"><label className="form-label fw-bold" style={{fontSize:12}}>Marriage</label>{isEd?<select className="form-select form-select-sm" value={rec.marriage} onChange={e=>updateField(idx,'marriage',e.target.value)}>{MARRIAGE_OPTS.map(o=><option key={o}>{o}</option>)}</select>:<div className="form-control-plaintext" style={{fontSize:13}}>{rec.marriage||'N/A'}</div>}</div>
                              <div className="col-md-1"><label className="form-label fw-bold" style={{fontSize:12}}>Blood Group</label>{isEd?<select className="form-select form-select-sm" value={rec.blood_group} onChange={e=>updateField(idx,'blood_group',e.target.value)}><option value="">--</option>{BLOOD_GROUPS.map(bg=><option key={bg} value={bg}>{bg}</option>)}</select>:<div className="form-control-plaintext" style={{fontSize:13,fontWeight:700,color:'#dc3545'}}>{rec.blood_group||'N/A'}</div>}</div>
                              <div className="col-md-3"><label className="form-label fw-bold" style={{fontSize:12}}>Address</label>{isEd?<input className="form-control form-control-sm" value={rec.address} onChange={e=>updateField(idx,'address',e.target.value)}/>:<div className="form-control-plaintext" style={{fontSize:13}}>{rec.address||'N/A'}</div>}</div>
                            </div>
                          </div>
                        </div>

                        <div className="card mb-3" style={{border:'1px solid #fbcfe8'}}>
                          <div className="card-header py-2" style={{background:'#fdf2f8'}}>
                            <h6 className="mb-0" style={{color:'#9d174d',fontSize:13}}><i className="ri-user-2-line me-2"></i>Husband</h6>
                          </div>
                          <div className="card-body py-2">
                            <div className="row g-2">
                              <div className="col-md-3"><label className="form-label fw-bold" style={{fontSize:12}}>Name</label>{isEd?<input className="form-control form-control-sm" value={rec.husband_name} onChange={e=>updateField(idx,'husband_name',e.target.value)}/>:<div className="form-control-plaintext" style={{fontSize:13}}>{rec.husband_name||'N/A'}</div>}</div>
                              <div className="col-md-1"><label className="form-label fw-bold" style={{fontSize:12}}>Age</label>{isEd?<input className="form-control form-control-sm" value={rec.husband_age} onChange={e=>updateField(idx,'husband_age',e.target.value)}/>:<div className="form-control-plaintext" style={{fontSize:13}}>{rec.husband_age||'N/A'}</div>}</div>
                              <div className="col-md-2"><label className="form-label fw-bold" style={{fontSize:12}}>Occupation</label>{isEd?<input className="form-control form-control-sm" value={rec.husband_occupation} onChange={e=>updateField(idx,'husband_occupation',e.target.value)}/>:<div className="form-control-plaintext" style={{fontSize:13}}>{rec.husband_occupation||'N/A'}</div>}</div>
                              <div className="col-md-2"><label className="form-label fw-bold" style={{fontSize:12}}>Marriage</label>{isEd?<select className="form-select form-select-sm" value={rec.husband_marriage} onChange={e=>updateField(idx,'husband_marriage',e.target.value)}>{MARRIAGE_OPTS.map(o=><option key={o}>{o}</option>)}</select>:<div className="form-control-plaintext" style={{fontSize:13}}>{rec.husband_marriage||'N/A'}</div>}</div>
                              <div className="col-md-1"><label className="form-label fw-bold" style={{fontSize:12}}>Blood Group</label>{isEd?<select className="form-select form-select-sm" value={rec.husband_blood_group} onChange={e=>updateField(idx,'husband_blood_group',e.target.value)}><option value="">--</option>{BLOOD_GROUPS.map(bg=><option key={bg} value={bg}>{bg}</option>)}</select>:<div className="form-control-plaintext" style={{fontSize:13,fontWeight:700,color:'#dc3545'}}>{rec.husband_blood_group||'N/A'}</div>}</div>
                              <div className="col-md-3"><label className="form-label fw-bold" style={{fontSize:12}}>Address</label>{isEd?<input className="form-control form-control-sm" value={rec.husband_address} onChange={e=>updateField(idx,'husband_address',e.target.value)}/>:<div className="form-control-plaintext" style={{fontSize:13}}>{rec.husband_address||'N/A'}</div>}</div>
                            </div>
                          </div>
                        </div>

                        <div className="row g-2 mb-3">
                          <div className="col-md-3">
                            <label className="form-label fw-bold" style={{fontSize:12}}>Consanguinity</label>
                            {isEd ? (
                              <select className="form-select form-select-sm" value={rec.consanguinity} onChange={e=>updateField(idx,'consanguinity',e.target.value)}>
                                <option value="">-- Select --</option>
                                {CONSANGUINITY_OPTS.map(o=><option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : <div className="form-control-plaintext" style={{fontSize:13}}>{rec.consanguinity||'N/A'}</div>}
                          </div>
                        </div>

                        <div style={{background:'linear-gradient(135deg,#fff7ed,#fef3c7)', border:'2px solid #f59e0b', borderRadius:10, padding:16, marginBottom:16}}>
                          <h6 style={{color:'#92400e', fontWeight:700, marginBottom:12}}><i className="ri-pregnant-woman-line me-2"></i>Pregnancy Summary</h6>
                          <div className="row g-2">
                            <div className="col-md-8">
                              <div className="row g-2">
                                <div className="col-md-3">
                                  <label className="form-label fw-bold" style={{fontSize:12}}>GPLA</label>
                                  {isEd ? (
                                    <div className="d-flex gap-1">
                                      <input className="form-control form-control-sm" value={formatGplaSubscript(rec.gpla_details)} readOnly style={{backgroundColor:'#fff',fontWeight:700,color:'#405189',cursor:'pointer'}} onClick={()=>{setGplaTargetIdx(idx);setShowGplaPopup(true)}}/>
                                      <button className="btn btn-sm btn-outline-primary" onClick={()=>{setGplaTargetIdx(idx);setShowGplaPopup(true)}}><i className="ri-calculator-line"></i></button>
                                    </div>
                                  ) : <div className="form-control-plaintext" style={{fontSize:16,fontWeight:700,color:'#405189'}}>{formatGplaSubscript(rec.gpla_details)||'N/A'}</div>}
                                </div>
                                <div className="col-md-2">
                                  <label className="form-label fw-bold" style={{fontSize:12}}>GA</label>
                                  <div className="form-control-plaintext" style={{fontSize:13,fontWeight:600,color:'#059669'}}>{rec.ga||calculateGA(rec.c_lmp||rec.lmp)||'—'}</div>
                                </div>
                                <div className="col-md-2">
                                  <label className="form-label fw-bold" style={{fontSize:12}}>Mode</label>
                                  {isEd?<select className="form-select form-select-sm" value={rec.mode} onChange={e=>updateField(idx,'mode',e.target.value)}><option>NATURAL</option><option>OI TI</option><option>IUI</option><option>ICSI</option></select>:<div className="form-control-plaintext" style={{fontSize:13}}>{rec.mode||'N/A'}</div>}
                                </div>
                                <div className="col-md-5">
                                  <label className="form-label fw-bold" style={{fontSize:12}}>Chief Complain</label>
                                  {isEd?<input className="form-control form-control-sm" value={rec.chief_complain} onChange={e=>updateField(idx,'chief_complain',e.target.value)}/>:<div className="form-control-plaintext" style={{fontSize:13}}>{rec.chief_complain||'N/A'}</div>}
                                </div>
                                <div className="col-md-3">
                                  <label className="form-label fw-bold" style={{fontSize:12}}>Risk Status</label>
                                  {isEd?<select className="form-select form-select-sm" value={rec.risk_status} onChange={e=>updateField(idx,'risk_status',e.target.value)} style={{backgroundColor:rec.risk_status==='Risk'?'#dc3545':'#198754',color:'#fff',fontWeight:700,border:'none'}}><option value="Normal" style={{backgroundColor:'#fff',color:'#000'}}>Normal</option><option value="Risk" style={{backgroundColor:'#fff',color:'#000'}}>Risk</option></select>:<span style={getRiskStyle(rec.risk_status)}>{rec.risk_status==='Risk'?'⚠ RISK':'✓ Normal'}</span>}
                                </div>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div style={{background:'#fff', border:'1px dashed #f59e0b', borderRadius:6, padding:8}}>
                                <div className="row g-1">
                                  <div className="col-12"><label className="form-label fw-bold mb-0" style={{fontSize:11}}>LMP</label>
                                    {isEd?<input type="date" className="form-control form-control-sm" style={{fontSize:11,padding:'2px 4px'}} value={rec.lmp||''} onChange={e=>updateDateField(idx,'lmp',e.target.value)}/>:<div style={{fontSize:12}}>{rec.lmp||'N/A'}</div>}
                                  </div>
                                  <div className="col-12"><label className="form-label fw-bold mb-0" style={{fontSize:11}}>EDD <small className="text-muted">(LMP+280d)</small></label>
                                    {isEd?<input type="date" className="form-control form-control-sm" style={{fontSize:11,padding:'2px 4px',backgroundColor:'#f0fdf4'}} value={rec.edd||''} onChange={e=>updateField(idx,'edd',e.target.value)}/>:<div style={{fontSize:12}}>{rec.edd||'N/A'}</div>}
                                  </div>
                                  <div className="col-12"><label className="form-label fw-bold mb-0" style={{fontSize:11}}>C-LMP</label>
                                    {isEd?<input type="date" className="form-control form-control-sm" style={{fontSize:11,padding:'2px 4px'}} value={rec.c_lmp||''} onChange={e=>updateDateField(idx,'c_lmp',e.target.value)}/>:<div style={{fontSize:12}}>{rec.c_lmp||'N/A'}</div>}
                                  </div>
                                  <div className="col-12"><label className="form-label fw-bold mb-0" style={{fontSize:11}}>C-EDD <small className="text-muted">(−280d)</small></label>
                                    {isEd?<input type="date" className="form-control form-control-sm" style={{fontSize:11,padding:'2px 4px',backgroundColor:'#fef2f2'}} value={rec.c_edd||''} onChange={e=>updateDateField(idx,'c_edd',e.target.value)}/>:<div style={{fontSize:12}}>{rec.c_edd||'N/A'}</div>}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {rec.risk_status==='Risk' && (
                            <div className="card mt-3" style={{border:'2px solid #dc3545',backgroundColor:'#fff5f5'}}>
                              <div className="card-header py-2" style={{backgroundColor:'#dc3545',color:'#fff'}}><h6 className="mb-0"><i className="ri-alert-line me-2"></i>High Risk Details</h6></div>
                              <div className="card-body py-2">
                                <div className="row">
                                  <div className="col-md-8"><label className="form-label fw-bold" style={{fontSize:13}}>Reasons (multi-select)</label>{isEd?<Select isMulti options={HIGH_RISK_REASONS} value={HIGH_RISK_REASONS.filter(o=>(rec.high_risk_reasons||[]).includes(o.value))} onChange={s=>updateField(idx,'high_risk_reasons',(s||[]).map(x=>x.value))} menuPortalTarget={document.body} closeMenuOnSelect={false} styles={{control:p=>({...p,minHeight:38,fontSize:13}),menuPortal:b=>({...b,zIndex:100002}),multiValue:b=>({...b,backgroundColor:'#fecaca'}),multiValueLabel:b=>({...b,color:'#991b1b',fontWeight:600,fontSize:11})}}/>:<div className="d-flex flex-wrap gap-1">{(rec.high_risk_reasons||[]).map((r,ri)=><span key={ri} className="badge" style={{backgroundColor:'#fecaca',color:'#991b1b',fontSize:11}}>{r}</span>)}</div>}</div>
                                  <div className="col-md-4"><label className="form-label fw-bold" style={{fontSize:13}}>Risk Remarks</label>{isEd?<textarea className="form-control form-control-sm" rows="2" value={rec.high_risk_remarks||''} onChange={e=>updateField(idx,'high_risk_remarks',e.target.value)} style={{borderColor:'#fca5a5'}}/>:<div style={{fontSize:13}}>{rec.high_risk_remarks||'N/A'}</div>}</div>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="row mt-3">
                            <div className="col-12">
                              <label className="form-label fw-bold" style={{fontSize:12}}>Remarks</label>
                              {isEd?<textarea className="form-control form-control-sm" rows="2" value={rec.remarks} onChange={e=>updateField(idx,'remarks',e.target.value)}/>:<div className="form-control-plaintext" style={{fontSize:13}}>{rec.remarks||'N/A'}</div>}
                            </div>
                          </div>

                          <div className="card mt-3" style={{border:'1px solid #a78bfa',background:'#faf5ff'}}>
                            <div className="card-header py-2" style={{background:'#ede9fe'}}>
                              <h6 className="mb-0" style={{color:'#6d28d9',fontSize:13}}><i className="ri-file-edit-line me-2"></i>Delivery Note</h6>
                            </div>
                            <div className="card-body py-2">
                              <div className="row g-2">
                                {[
                                  {k:'date', label:'Date', type:'date', col:2},
                                  {k:'time', label:'Time', type:'time', col:2},
                                  {k:'sex', label:'Sex', type:'select', opts:['MALE','FEMALE'], col:2},
                                  {k:'baby_wt', label:'Baby Wt (kg)', type:'text', col:2},
                                  {k:'hospital_name', label:'Hospital', type:'text', col:4},
                                  {k:'full_pre_term', label:'Full/Pre Term', type:'select', opts:['FULL TERM','PRE TERM'], col:3},
                                  {k:'mode', label:'Mode', type:'select', opts:['NVD','C-SECTION','INSTRUMENT'], col:3},
                                  {k:'outcome', label:'Outcome', type:'select', opts:['LIVE','DEAD','STILL BIRTH','IUD'], col:3},
                                  {k:'complication', label:'Complication', type:'select', opts:['HYPERTENSION','ECLAMPSIA','GDM','ANEMIA','PPH','PLACENTA PRAEVIA','ABRUPTION','NONE'], col:3},
                                  {k:'remarks', label:'Remarks', type:'text', col:12},
                                ].map(f => {
                                  const val = rec.delivery_note?.[f.k] || '';
                                  const onCh = v => updateField(idx, 'delivery_note', { ...(rec.delivery_note||{}), [f.k]: v });
                                  return (
                                    <div key={f.k} className={`col-md-${f.col}`}>
                                      <label className="form-label fw-bold" style={{fontSize:11}}>{f.label}</label>
                                      {isEd ? (
                                        f.type === 'select'
                                          ? <select className="form-select form-select-sm" style={{fontSize:12}} value={val} onChange={e=>onCh(e.target.value)}>
                                              <option value="">--</option>
                                              {f.opts.map(o=><option key={o} value={o}>{o}</option>)}
                                            </select>
                                          : <input type={f.type} className="form-control form-control-sm" style={{fontSize:12}} value={val} onChange={e=>onCh(e.target.value)}/>
                                      ) : <div className="form-control-plaintext" style={{fontSize:12}}>{val||'—'}</div>}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>

                        <small className="text-muted"><i className="ri-time-line me-1"></i>Timestamp: {formatTs(rec.created_at)}</small>
                      </div>
                    )}

                    <div style={{padding:20}}>
                      {activeSection==='menstrual' && <MenstrualHistorySection data={rec.menstrual_history||{}} onChange={d=>updateField(idx,'menstrual_history',d)} isEditing={isEd}/>}

                      {activeSection==='obstetric' && (
                        <ObstetricHistorySection
                          rows={rec.obstetric_history||[{...defaultObRow}]}
                          onChange={r=>updateField(idx,'obstetric_history',r)}
                          isEditing={isEd}
                          onAddRow={()=>{if((rec.obstetric_history||[]).length>=10){toast.warning("Max 10 rows");return;} updateField(idx,'obstetric_history',[...(rec.obstetric_history||[]),{...defaultObRow}]);}}
                          onRemoveRow={ri=>updateField(idx,'obstetric_history',(rec.obstetric_history||[]).filter((_,i)=>i!==ri))}/>
                      )}

{activeSection==='investigation' && (
  <InvestigationSection 
    rows={rec.investigations||[]} 
    onChange={r=>updateField(idx,'investigations',r)} 
    isEditing={isEd}
    immunizations={rec.immunizations||[]}
    onImmunizationChange={r=>updateField(idx,'immunizations',r)}


    
  />
)}

                      {/* {activeSection==='immunization' && <ImmunizationSection rows={rec.immunizations||[]} onChange={r=>updateField(idx,'immunizations',r)} isEditing={isEd}/>} */}

                      {activeSection==='general_exam' && (
                        <GeneralExamSection 
                          rows={rec.general_exams||[]} 
                          onChange={r=>updateField(idx,'general_exams',r)} 
                          isEditing={isEd}
                          lmpRef={rec.c_lmp || rec.lmp || ''}
                          onNewMedicine={(geIdx) => openNewMedicine(idx, geIdx)}
                          onOldMedicine={(geIdx) => openOldMedicine(idx, geIdx)}
                          onPrintMedicine={(geIdx) => printGeMedicines(idx, geIdx)}
                          onViewPrescription={pid=>toast.info(`View prescription: ${pid}`)}
                          onUploadPrescription={(ri,file)=>{
                            const reader=new FileReader();
                            reader.onload=e=>{
                              const updated=[...(rec.general_exams||[])];
                              updated[ri]={...updated[ri],prescription_file:e.target.result};
                              updateField(idx,'general_exams',updated);
                              toast.success("File attached");
                            };
                            reader.readAsDataURL(file);
                          }}/>
                      )}

                      {activeSection==='past_history' && <KeyValueHistorySection title="Past History" icon="ri-history-line" types={PAST_HISTORY_TYPES} data={rec.past_history||{}} onChange={d=>updateField(idx,'past_history',d)} isEditing={isEd}/>}
                      {activeSection==='family_history' && <KeyValueHistorySection title="Family History" icon="ri-group-line" types={FAMILY_HISTORY_TYPES} data={rec.family_history||{}} onChange={d=>updateField(idx,'family_history',d)} isEditing={isEd} headerBg="#d4edda" color="#155724" borderColor="#c3e6cb"/>}
                      {activeSection==='personal_history' && <KeyValueHistorySection title="Personal History" icon="ri-user-heart-line" types={PERSONAL_HISTORY_TYPES} data={rec.personal_history||{}} onChange={d=>updateField(idx,'personal_history',d)} isEditing={isEd} headerBg="#fff3cd" color="#856404" borderColor="#ffc107"/>}

                      {isEd && (!rec.is_closed || isAdmin) && (
                        <div className="d-flex justify-content-end gap-2 mt-3 pt-2" style={{borderTop:'1px solid #e5e7eb'}}>
                          {rec.isNew?<button className="btn btn-secondary btn-sm" onClick={()=>setRecords(p=>p.filter((_,i)=>i!==idx))}><i className="ri-close-line me-1"></i>Cancel</button>
                            :<button className="btn btn-secondary btn-sm" onClick={()=>toggleEdit(idx)}><i className="ri-close-line me-1"></i>Cancel Edit</button>}
                          <button className="btn btn-success btn-sm" onClick={()=>saveRecord(idx)} disabled={saving}><i className="ri-save-line me-1"></i>{saving?'Saving...':'Save Record'}</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* MEDICINE POPUP */}
          {showMedPopup && (() => {
            const isReadOnly = medPopupMode === 'old';
            return (
              <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100001}} onClick={()=>setShowMedPopup(false)}>
                <div style={{background:'#fff',borderRadius:12,width:'95%',maxWidth:1200,maxHeight:'90vh',boxShadow:'0 10px 40px rgba(0,0,0,0.3)',overflow:'hidden',display:'flex',flexDirection:'column'}} onClick={e=>e.stopPropagation()}>
                  <div style={{background: isReadOnly ? 'linear-gradient(135deg,#3b82f6,#2563eb)' : 'linear-gradient(135deg,#10b981,#059669)', color:'#fff', padding:'14px 20px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <h5 style={{margin:0}}>
                      <i className={`${medPopupMode==='new'?'ri-add-circle-line':'ri-history-line'} me-2`}></i>
                      {medPopupMode==='new' ? 'Add New Medicines' : 'Last Prescription — Read-Only'}
                    </h5>
                    <button onClick={()=>setShowMedPopup(false)} style={{background:'none',border:'none',color:'#fff',fontSize:24,cursor:'pointer'}}>&times;</button>
                  </div>
                  <div style={{padding:16,overflowY:'auto',flex:1}}>
                    {medPopupMode === 'new' && (
                      <div className="card mb-3" style={{border:'1px solid #c7d2fe'}}>
                        <div className="card-body py-2">
                          <div className="row align-items-center">
                            <div className="col-md-2"><strong style={{fontSize:13}}><i className="ri-file-copy-line me-1"></i>Template:</strong></div>
                            <div className="col-md-10">
                              <Select 
                                options={templateOptions} 
                                value={medPopupTemplate}
                                onChange={applyTemplateToPopup}
                                placeholder="Pick a template to prefill medicines..." 
                                isClearable 
                                menuPortalTarget={document.body}
                                styles={{menuPortal:b=>({...b,zIndex:100002}), control:p=>({...p,minHeight:36,fontSize:13})}}
                                formatOptionLabel={o=>(<div><strong>{o.label}</strong>{o.doctor&&<small className="text-muted ms-2">by {o.doctor}</small>}</div>)}
                                isLoading={medPopupLoading}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {medPopupMode === 'old' && (
                      <div className="alert alert-info py-2 mb-2" style={{fontSize:13}}>
                        <i className="ri-information-line me-2"></i>
                        <strong>Read-only view</strong> — Last OPD prescription for reference. To add fresh meds, close and click <strong>"New Medicine"</strong>.
                      </div>
                    )}
                    {medPopupLoading ? (
                      <div className="text-center py-4"><div className="spinner-border text-primary"></div><p className="mt-2">Loading...</p></div>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between mb-2">
                          <strong style={{color:'#405189'}}>Medicines ({medPopupRows.length})</strong>
                          {!isReadOnly && <button className="btn btn-primary btn-sm" onClick={medPopupAdd}><i className="ri-add-line me-1"></i>Add Row</button>}
                        </div>
                        <div className="table-responsive" style={{maxHeight:'50vh',overflowY:'auto'}}>
                          <table className="table table-bordered table-sm" style={{fontSize:12}}>
                            <thead style={{background:'#e8eef6', position:'sticky', top:0, zIndex:2}}>
                              <tr>
                                <th style={{width:40}}>#</th>
                                <th style={{minWidth:280}}>Medicine</th>
                                <th style={{width:60}}>M</th><th style={{width:60}}>N</th><th style={{width:60}}>E</th><th style={{width:60}}>Night</th>
                                <th style={{width:70}}>Days</th><th style={{width:70}}>Qty</th>
                                <th style={{minWidth:180}}>Instructions</th>
                                {!isReadOnly && <th style={{width:50}}></th>}
                              </tr>
                            </thead>
                            <tbody>
                              {medPopupRows.length === 0 && <tr><td colSpan={isReadOnly ? 9 : 10} className="text-center text-muted py-3">No medicines.</td></tr>}
                              {medPopupRows.map((row, ri) => (
                                <tr key={ri}>
                                  <td className="text-center">{ri+1}</td>
                                  <td>
                                    <Select 
                                      options={medicineOptions}
                                      value={medicineOptions.find(o => o.label === row.medicine_name) || (row.medicine_name ? {label:row.medicine_name, value:row.medicine_id} : null)}
                                      onChange={sel => {
                                        if (!sel) { medPopupUpd(ri, 'medicine_name', ''); medPopupUpd(ri, 'medicine_id', ''); return; }
                                        setMedPopupRows(prev => prev.map((r,i)=>i===ri?{...r, medicine_name:sel.label, medicine_id:sel.value, isSyrup:!!sel.isSyrup, qty: sel.isSyrup ? '1' : r.qty}:r));
                                      }}
                                      placeholder="Search medicine..."
                                      isClearable isSearchable
                                      isDisabled={isReadOnly}
                                      menuPortalTarget={document.body}
                                      menuPlacement="auto" menuPosition="fixed"
                                      styles={{control:p=>({...p,minHeight:32,fontSize:12, backgroundColor: isReadOnly ? '#f8f9fa' : '#fff'}), menuPortal:b=>({...b,zIndex:100003})}}
                                    />
                                  </td>
                                  <td><input type="number" step="0.5" min="0" disabled={isReadOnly} className="form-control form-control-sm" style={{fontSize:11,padding:'2px 4px'}} value={row.morning} onChange={e=>medPopupUpd(ri,'morning',e.target.value)}/></td>
                                  <td><input type="number" step="0.5" min="0" disabled={isReadOnly} className="form-control form-control-sm" style={{fontSize:11,padding:'2px 4px'}} value={row.noon} onChange={e=>medPopupUpd(ri,'noon',e.target.value)}/></td>
                                  <td><input type="number" step="0.5" min="0" disabled={isReadOnly} className="form-control form-control-sm" style={{fontSize:11,padding:'2px 4px'}} value={row.evening} onChange={e=>medPopupUpd(ri,'evening',e.target.value)}/></td>
                                  <td><input type="number" step="0.5" min="0" disabled={isReadOnly} className="form-control form-control-sm" style={{fontSize:11,padding:'2px 4px'}} value={row.night} onChange={e=>medPopupUpd(ri,'night',e.target.value)}/></td>
                                  <td><input type="number" min="1" disabled={isReadOnly} className="form-control form-control-sm" style={{fontSize:11,padding:'2px 4px'}} value={row.days} onChange={e=>medPopupUpd(ri,'days',e.target.value)}/></td>
                                  <td><input type="text" className="form-control form-control-sm text-center fw-bold" style={{fontSize:11,padding:'2px 4px',backgroundColor:'#d4edda',color:'#155724'}} value={row.qty} readOnly/></td>
                                  <td><input type="text" disabled={isReadOnly} className="form-control form-control-sm" style={{fontSize:11,padding:'2px 4px'}} value={row.instructions} onChange={e=>medPopupUpd(ri,'instructions',e.target.value)} placeholder="e.g. After food"/></td>
                                  {!isReadOnly && <td className="text-center"><button className="btn btn-sm btn-outline-danger p-0" style={{width:22,height:22}} onClick={()=>medPopupRem(ri)} disabled={medPopupRows.length===1}><i className="ri-close-line"></i></button></td>}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                  <div style={{padding:12, borderTop:'1px solid #e5e7eb', display:'flex', justifyContent:'flex-end', gap:8, background:'#f8f9fa'}}>
                    <button className="btn btn-secondary" onClick={()=>setShowMedPopup(false)}>{isReadOnly ? 'Close' : 'Cancel'}</button>
                    {!isReadOnly && <button className="btn btn-success" onClick={saveMedPopup} disabled={medPopupLoading || medPopupRows.filter(r=>r.medicine_name?.trim()).length === 0}><i className="ri-save-line me-1"></i>Save</button>}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* CLOSE MODAL */}
          {showCloseModal && (
            <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100001}} onClick={()=>setShowCloseModal(false)}>
              <div style={{background:'#fff',borderRadius:12,width:'90%',maxWidth:520,boxShadow:'0 10px 40px rgba(0,0,0,0.25)',overflow:'hidden'}} onClick={e=>e.stopPropagation()}>
                <div style={{background:'linear-gradient(135deg,#f59e0b,#dc2626)',color:'#fff',padding:'14px 20px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <h5 style={{margin:0}}><i className="ri-lock-line me-2"></i>Close ANC File</h5>
                  <button onClick={()=>setShowCloseModal(false)} style={{background:'none',border:'none',color:'#fff',fontSize:24,cursor:'pointer'}}>&times;</button>
                </div>
                <div style={{padding:20}}>
                  <div className="alert alert-warning py-2" style={{fontSize:13}}><i className="ri-alert-line me-2"></i>Once closed, only admin can reopen/edit.</div>
                  <label className="form-label fw-bold">Reason for closing <span className="text-danger">*</span></label>
                  <textarea className="form-control" rows="4" placeholder="E.g. Delivery completed, transferred..." value={closeReason} onChange={e=>setCloseReason(e.target.value)} autoFocus/>
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button className="btn btn-secondary" onClick={()=>setShowCloseModal(false)}>Cancel</button>
                    <button className="btn btn-danger" onClick={confirmCloseAnc} disabled={!closeReason.trim()}><i className="ri-lock-line me-1"></i>Close File</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PRINT MODAL */}
          {showPrintModal && (
            <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100001}} onClick={()=>setShowPrintModal(false)}>
              <div style={{background:'#fff',borderRadius:12,width:'90%',maxWidth:520,boxShadow:'0 10px 40px rgba(0,0,0,0.25)',overflow:'hidden'}} onClick={e=>e.stopPropagation()}>
                <div style={{background:'linear-gradient(135deg,#405189,#6366f1)',color:'#fff',padding:'14px 20px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <h5 style={{margin:0}}><i className="ri-printer-line me-2"></i>Select Sections to Print</h5>
                  <button onClick={()=>setShowPrintModal(false)} style={{background:'none',border:'none',color:'#fff',fontSize:24,cursor:'pointer'}}>&times;</button>
                </div>
                <div style={{padding:20}}>
                  <div className="d-flex gap-2 mb-3">
                    <button className="btn btn-sm btn-outline-primary" onClick={()=>toggleAllPrintSections(true)}><i className="ri-checkbox-multiple-line me-1"></i>Select All</button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={()=>toggleAllPrintSections(false)}><i className="ri-close-circle-line me-1"></i>Clear All</button>
                  </div>
                  <div className="row g-2">
                    {[
                      {k:'basic',label:'Basic Info',icon:'ri-user-line'},
                      {k:'menstrual',label:'Menstrual History',icon:'ri-calendar-2-line'},
                      {k:'obstetric',label:'Obstetric History',icon:'ri-stethoscope-line'},
                      {k:'investigation',label:'Investigation',icon:'ri-test-tube-line'},
                      {k:'immunization',label:'Immunization',icon:'ri-syringe-line'},
                      {k:'general_exam',label:'General Exam',icon:'ri-heart-pulse-line'},
                      {k:'past_history',label:'Past History',icon:'ri-history-line'},
                      {k:'family_history',label:'Family History',icon:'ri-group-line'},
                      {k:'personal_history',label:'Personal History',icon:'ri-user-heart-line'},
                    ].map(t => (
                      <div className="col-md-6" key={t.k}>
                        <label style={{display:'flex',alignItems:'center',padding:'8px 12px',border:`1px solid ${printSections[t.k]?'#405189':'#e5e7eb'}`,borderRadius:6,cursor:'pointer',background:printSections[t.k]?'#eef2ff':'#fff',fontSize:13}}>
                          <input type="checkbox" checked={!!printSections[t.k]} onChange={()=>togglePrintSection(t.k)} style={{marginRight:8}}/>
                          <i className={`${t.icon} me-2`} style={{color:'#405189'}}></i>
                          {t.label}
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button className="btn btn-secondary" onClick={()=>setShowPrintModal(false)}>Cancel</button>
                    <button className="btn btn-primary" onClick={doPrint} disabled={Object.values(printSections).filter(v=>v).length===0}><i className="ri-printer-line me-1"></i>Print ({Object.values(printSections).filter(v=>v).length})</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer style={{backgroundColor:'#f0f4ff',borderTop:'2px solid #c7d2fe'}}>
          <button className="btn btn-secondary" onClick={onHide}><i className="ri-close-line me-1"></i>Close</button>
        </Modal.Footer>
      </Modal>

      <GplaScorePopup show={showGplaPopup} onClose={()=>{setShowGplaPopup(false);setGplaTargetIdx(null);}} onDone={handleGplaDone} initialValues={gplaTargetIdx!==null?records[gplaTargetIdx]?.gpla_details:{}}/>
    </>
  );
}

export default AncFileModal;